/**
 * Chat message handling for Ragsmith Web UI
 */

import {
    state,
    saveSessionStateWithModel,
    saveNarratorProgress,
    recordFailure,
    recordSuccess,
    getCooldownRemaining,
    setLastSentMessage,
    clearLastSentMessage,
    COOLDOWN_MS,
    clearCooldown
} from './state.js';
import {
    elements,
    addMessage,
    addSystemMessage,
    showLoading,
    hideLoading,
    setStreamingState,
    scrollToBottom,
    addMetaToMessage,
    showLoadingOverlay,
    updateLoadingProgress,
    hideLoadingOverlay,
    updateModeIndicators,
    updateMessageContent,
    renderMessageMarkdown,
    addHeaderToMessage,
    showCollectionHeader,
    hideCollectionHeader,
    showRoutingMatches,
    showToolsUsed,
    showImageSearchResults,
    addAnalyticsToMessage,
    addLoadingStep,
    updateLoadingStep,
    updateStepSubstep,
    solidifyLoadingSteps,
    renderDataBlocks,
    showStopButton,
    markMessageCancelled,
    updateLoadingText,
    addThinkingToMessage,
    hasThinkingContent,
    separateThinkingContent,
    clearChat,
    updateExtractionStatus,
    updateCitations,
    startCooldownUI,
    clearCooldownUI,
    loadedModelSupportsVision
} from './ui.js';
import { updateMenuLoadButton, updateHeaderStatus, getMenuElements, getSelectedCollectionTitle, updateFidelitySliderState, applyAgentSliderOverrides } from './menu.js';
import { setToolingRequired, isBypassed, setBypassed } from './model-manager.js';
import { showToast } from './toast.js';
import * as api from './api.js';
import { escapeHtml } from './html-escape.js';

import { handleCommand } from './commands.js';
import { getAttachments, clearAttachments, hasPendingUploads } from './input.js';
import { isVoiceActive, isVoiceNarratorActive, autoPlayNarratorTts, updateVoiceBtnIcon, highlightNarratorMessage, clearNarratorHighlight, updateNextButtonLabel, onVoiceResponseComplete, onVoiceStreamChunk, onVoiceToolEvent, onVoiceToolInterrupt, onVoiceRetract, resetToolCallPitch, stopVoiceMode } from './voice.js';
import { streamTtsPlayback } from './tts-player.js';

// Legacy no-op. The dedicated spinner stop button is gone; the send button
// itself is now the stop button. Kept so the ~8 existing call sites scattered
// through chat.js (tool/search status branches) don't have to be touched in
// this diff.
function _wireStopButton() { /* intentionally empty */ }

/**
 * Stop everything currently in flight: chat stream (server-side cancel),
 * voice modes, narrator TTS autoplay. The send button transforms into the
 * stop button via setStreamingState() and routes its click here.
 *
 * The actual cleanup of UI state (button class swap, spinner removal)
 * happens when the SSE stream's onComplete fires — server emits a final
 * `cancelled: true` event after request_cancel() lands, and the existing
 * completion paths call setStreamingState(false).
 */
export async function stopAll() {
    console.log('[stopAll] fired at', new Date().toISOString().slice(11, 23));
    updateLoadingText('Stopping...');

    // Server-side cancel — flips _cancel_requested, breaks tool loops,
    // closes the SSE stream. Same call the old spinner stop button made.
    try { await api.cancelChat(); } catch {}

    // Voice flows — if either is active, tear it down. The voice module's
    // own stop functions handle WebSocket close, audio track release,
    // narrator TTS abort, and mediaSession cleanup. stopVoiceMode already
    // routes to dictation cleanup when dictation is active, so calling both
    // is safe even when only one is running.
    try {
        if (isVoiceActive() || isVoiceNarratorActive()) {
            stopVoiceMode();
        }
    } catch {}
}

/**
 * Decorate the rendered text for a cancelled response.
 *   empty → "*Response stopped.*"
 *   non-empty → original text + "\n\n*stopped.*"
 * The suffix is intentionally minimal — readers should see their partial
 * text exactly as it was streamed, with a quiet italic note that it ended.
 */
function _decorateCancelledContent(text) {
    const t = (text || '').trim();
    if (!t) return '*Response stopped.*';
    return text + '\n\n*stopped.*';
}

/**
 * Estimate token count from text (rough approximation: ~4 chars per token)
 */
function estimateTokens(text) {
    if (!text) return 0;
    return Math.ceil(text.length / 4);
}

/**
 * Get provider type from model tier/id
 */
function getProviderFromModel(model) {
    if (!model) return 'local';
    // Check dropdown's data-provider first (set by model-manager from server data)
    const modelSelect = document.getElementById('menu-model-select');
    if (modelSelect) {
        for (const opt of modelSelect.options) {
            if (opt.value === model || opt.dataset?.modelName === model) {
                if (opt.dataset?.provider) return opt.dataset.provider;
            }
        }
    }
    // Fallback to pattern matching
    if (model === 'haiku' || model === 'sonnet' || model === 'opus' || model.startsWith('claude-')) return 'anthropic';
    if (model.includes('/') || model === 'r1' || model === 'deepseek') return 'openrouter';
    if (model.startsWith('groq:')) return 'groq';
    return 'local';
}

// --- Per-message analytics helpers ---

let _pricingCache = null;

// The turn most recently dispatched, for error persistence. Set at send time
// by both send paths; read when a stream error is saved, so the user's
// prompt and attachment references survive a turn that dies before the
// server persists them (media pre-processing runs first and can be long).
let lastDispatchedTurn = null;
let _pricingFetchPromise = null;

async function getPricing() {
    if (_pricingCache) return _pricingCache;
    if (_pricingFetchPromise) return _pricingFetchPromise;
    // Local models have no pricing — skip the fetch to avoid 401 noise on remote hosts
    if (state.loadedProvider === 'local') return {};
    _pricingFetchPromise = (async () => {
        try {
            const resp = await fetch('/models/pricing', { headers: api.getHeaders(false) });
            if (!resp.ok) { _pricingFetchPromise = null; return {}; }
            const data = await resp.json();
            _pricingCache = data.pricing || data;
            return _pricingCache;
        } catch {
            return {};
        }
    })();
    return _pricingFetchPromise;
}

function calculateCost(model, promptTokens, completionTokens, pricing) {
    if (!pricing || !model) return null;
    const p = pricing[model];
    if (!p) return null;
    const input = p.input || p.prompt || 0;
    const output = p.output || p.completion || 0;
    return (promptTokens / 1_000_000 * input) + (completionTokens / 1_000_000 * output);
}

async function buildAndSaveMetadata(assistantDiv, data, message, sessionId) {
    const pricing = await getPricing();
    const model = data.model || state.loadedModel;
    const modelId = data.model_id || state.loadedModel;  // model_id for pricing lookup
    const promptTokens = data.token_usage?.prompt_tokens || estimateTokens(message);
    const completionTokens = data.token_usage?.completion_tokens || estimateTokens(data.full_text);
    const totalTokens = promptTokens + completionTokens;
    const cost = calculateCost(modelId, promptTokens, completionTokens, pricing);

    const metadata = {
        model: model,
        prompt_tokens: promptTokens,
        completion_tokens: completionTokens,
        total_tokens: totalTokens,
        cost: cost,
        response_time_ms: data.response_time_ms || null,
        turn_summary: data.turn_summary || null
    };

    // Show analytics UI
    addAnalyticsToMessage(assistantDiv, metadata);

    // Persist to DB (fire-and-forget)
    if (sessionId) {
        fetch(`/sessions/${sessionId}/messages/last/metadata`, {
            method: 'PATCH',
            headers: api.getHeaders(),
            body: JSON.stringify({ metadata })
        }).catch(() => {});
    }
}

/**
 * Track model usage after a successful chat
 * @param {string} model - Model identifier
 * @param {string} promptText - The user's prompt text
 * @param {string} responseText - The assistant's response text
 * @param {string} sessionId - Session ID
 * @param {string} callType - Type of LLM call (default: "chat")
 * @param {object} [tokenUsage] - Optional real token counts {prompt_tokens, completion_tokens}
 */
async function trackModelUsage(model, promptText, responseText, sessionId, callType = 'chat', tokenUsage = null) {
    try {
        // If server already recorded real tool_loop tokens, skip frontend estimation
        if (tokenUsage && callType === 'tool_loop') return;

        const provider = getProviderFromModel(model);
        const promptTokens = tokenUsage ? tokenUsage.prompt_tokens : estimateTokens(promptText);
        const completionTokens = tokenUsage ? tokenUsage.completion_tokens : estimateTokens(responseText);

        await api.recordModelUsage(
            model,
            provider,
            promptTokens,
            completionTokens,
            sessionId,
            true,
            callType
        );
    } catch {
        // Don't let usage tracking errors affect the chat
    }
}

/**
 * Poll extraction status and update the UI when complete.
 * This enables the delete button once fact extraction finishes.
 *
 * @param {string} sessionId - Session ID to check
 * @param {HTMLElement} messageEl - The assistant message element to update
 * @param {number} maxAttempts - Maximum polling attempts (default: 30 = ~30 seconds)
 */
async function pollExtractionStatus(sessionId, messageEl, maxAttempts = 30) {
    if (!sessionId || !messageEl) return;

    let attempts = 0;
    const pollInterval = 1000; // 1 second

    const poll = async () => {
        try {
            const response = await fetch(`/sessions/${sessionId}/extraction_status`, {
                headers: api.getHeaders(false)
            });
            if (!response.ok) {
                return;
            }

            const data = await response.json();

            if (!data.extracting && data.latest_message_id) {
                // Extraction complete - enable delete button
                updateExtractionStatus(messageEl, data.latest_message_id);
                return;
            }

            // Still extracting - poll again
            attempts++;
            if (attempts < maxAttempts) {
                setTimeout(poll, pollInterval);
            } else {
                // Timeout - still enable delete with whatever ID we have
                if (data.latest_message_id) {
                    updateExtractionStatus(messageEl, data.latest_message_id);
                }
            }
        } catch {
        }
    };

    // Start polling after a short delay (give server time to save message)
    setTimeout(poll, 500);
}

/**
 * Get display name for the assistant (agent name or model name)
 * Always uses state.loadedModel as the source of truth for model name
 */
function getAssistantName(agentName) {
    if (agentName) {
        // Convert "search-agent" to "Search Agent"
        return agentName.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    }

    // No agent - use loaded model name (e.g., "Qwen", "Gemma", "Llama")
    // IMPORTANT: Always use state.loadedModel, not dropdown value
    const loadedModel = state.loadedModel;
    if (loadedModel) {
        // Try to get display name from Model Manager's stored data first
        if (state.selectedModelData?.display_name && state.selectedModel === loadedModel) {
            const displayName = state.selectedModelData.display_name;
            const match = displayName.match(/^([A-Za-z]+)/);
            if (match) return match[1];
            return displayName;
        }

        // Extract model family from the model identifier
        // e.g., "meta-llama/llama-3.3-70b-instruct:free" -> "Llama"
        // e.g., "qwen2.5:3b" -> "Qwen"
        // e.g., "claude-sonnet-4-6" -> "Claude"
        let modelName = loadedModel;
        if (loadedModel.includes('/')) {
            // OpenRouter format: provider/model-name
            modelName = loadedModel.split('/')[1] || loadedModel;
        }
        // Extract the model family (first alphabetic word, capitalize)
        const match = modelName.match(/^([a-zA-Z]+)/);
        if (match) {
            return match[1].charAt(0).toUpperCase() + match[1].slice(1).toLowerCase();
        }
        return modelName;
    }

    return 'Ragsmith';
}

/**
 * Throttle state for markdown rendering during streaming
 * Uses throttling (render at regular intervals) instead of debouncing
 * (wait for pause) to ensure smooth output even with fast models.
 */
let lastRenderTime = 0;
let pendingRenderFrame = null;
let pendingRenderTimer = null;
let renderGeneration = 0; // Monotonic counter to invalidate stale scheduled renders
const RENDER_INTERVAL = 50; // ms between renders - lower = smoother but more CPU

/**
 * Throttled render function - ensures smooth streaming even with fast models.
 * Renders immediately if enough time has passed, otherwise schedules via rAF.
 */
function throttledRender(assistantDiv, fullText) {
    const now = performance.now();
    const timeSinceLastRender = now - lastRenderTime;

    // If enough time has passed, render immediately
    if (timeSinceLastRender >= RENDER_INTERVAL) {
        lastRenderTime = now;
        renderMessageMarkdown(assistantDiv, fullText);
        scrollToBottom();
        return;
    }

    // Cancel any pending scheduled render
    if (pendingRenderFrame) {
        cancelAnimationFrame(pendingRenderFrame);
        pendingRenderFrame = null;
    }
    if (pendingRenderTimer) {
        clearTimeout(pendingRenderTimer);
        pendingRenderTimer = null;
    }

    // Schedule render at the next opportunity after the interval.
    // Use a generation counter so stale callbacks (from rAF/setTimeout race) are no-ops.
    const gen = ++renderGeneration;
    const delay = RENDER_INTERVAL - timeSinceLastRender;
    pendingRenderFrame = requestAnimationFrame(() => {
        pendingRenderFrame = null;
        pendingRenderTimer = setTimeout(() => {
            pendingRenderTimer = null;
            if (gen !== renderGeneration) return; // Stale — a newer render was scheduled
            lastRenderTime = performance.now();
            renderMessageMarkdown(assistantDiv, fullText);
            scrollToBottom();
        }, delay);
    });
}

/**
 * Cancel any pending scheduled render (call on stream complete before final render)
 */
function flushPendingRender() {
    if (pendingRenderFrame) {
        cancelAnimationFrame(pendingRenderFrame);
        pendingRenderFrame = null;
    }
    if (pendingRenderTimer) {
        clearTimeout(pendingRenderTimer);
        pendingRenderTimer = null;
    }
    renderGeneration++; // Invalidate any in-flight callbacks
    lastRenderTime = 0;
}

/**
 * Run initial prompt for an agent (no user message shown)
 */
export async function runInitialPrompt(agent, settings) {
    if (state.isStreaming) return;
    state.isStreaming = true;
    setStreamingState(true);
    showLoading();

    let assistantDiv = null;
    let fullText = '';
    const assistantName = getAssistantName(agent);
    const autoplay = settings?.autoplay_initial_prompt || false;

    // For autoplay mode: buffer text, start TTS, wait for preload, then render
    let ttsController = null;
    let bufferedChunks = [];
    let streamComplete = false;
    let completeData = null;
    let autoplayDone = null;  // Promise that resolves when autoplay rendering completes
    let resolveAutoplayDone = null;
    if (autoplay) {
        autoplayDone = new Promise(r => { resolveAutoplayDone = r; });
    }

    try {
        await api.sendChatMessage(
            {
                message: '',
                model: state.loadedModel || null,
                sessionId: state.sessionId,
                agent: agent,
                temperature: state.temperature,
                fidelity: state.fidelity,
                memoryLevel: state.memoryLevel,
                kbThreshold: state.agentSettings?.agent_kb_threshold ?? undefined,
                debugMode: state.debugMode,
                narratorMode: state.narratorMode,
                narratorState: state.narratorState,
                disabledTools: [...state.disabledTools],
                initialPrompt: true
            },
            // onChunk
            (chunk) => {
                fullText += chunk;

                if (autoplay) {
                    // Buffer chunks — don't render yet (wait for TTS preload)
                    bufferedChunks.push(chunk);
                } else {
                    // Normal streaming render
                    if (!assistantDiv) {
                        hideLoading();
                        assistantDiv = addMessage('assistant', '', { header: assistantName, extracting: false });
                        if (state.narratorMode) highlightNarratorMessage(assistantDiv);
                    }

                    // Feed text to voice for progressive TTS (if voice mode active)
                    if (isVoiceActive()) onVoiceStreamChunk(chunk);

                    throttledRender(assistantDiv, fullText);
                }
            },
            // onComplete
            async (data) => {
                flushPendingRender();
                completeData = data;
                streamComplete = true;

                if (autoplay) {
                    // Start TTS with full text, wait for preload
                    ttsController = streamTtsPlayback(data.full_text);
                    await ttsController.ready;

                    // NOW render the message
                    hideLoading();
                    assistantDiv = addMessage('assistant', '', { header: assistantName, extracting: false });
                    if (state.narratorMode) highlightNarratorMessage(assistantDiv);
                    renderMessageMarkdown(assistantDiv, data.full_text);

                    // Add speaker icon (unmuted for autoplay)
                    _addSpeakerIcon(assistantDiv, data.full_text, ttsController, false);
                    resolveAutoplayDone();
                } else {
                    // Normal: final render
                    if (!assistantDiv) {
                        hideLoading();
                        assistantDiv = addMessage('assistant', '', { header: assistantName, extracting: false });
                    }
                    renderMessageMarkdown(assistantDiv, data.full_text);

                    // Add speaker icon (muted — user can click to play)
                    _addSpeakerIcon(assistantDiv, data.full_text, null, true);
                }

                if (data.session_id) {
                    state.sessionId = data.session_id;
                    saveSessionStateWithModel(state.loadedModel);
                }

                // Update narrator state and DOM attributes from response
                let metaText = '';
                if (data.narrator) {
                    // Set DOM attributes FIRST so badge count picks them up
                    if (assistantDiv) {
                        assistantDiv.dataset.narratorChunkId = data.narrator.chunk_id;
                        assistantDiv.dataset.narratorTotalChunks = data.narrator.total_chunks;
                        assistantDiv.dataset.narratorSection = data.narrator.section;
                        assistantDiv.dataset.narratorBook = data.narrator.book || '';
                        assistantDiv.dataset.narratorKbPath = data.narrator.kb_path || '';
                        assistantDiv.dataset.narratorChunkCount = data.narrator.chunk_count || 1;
                    }
                    metaText += `${data.narrator.section} (${data.narrator.chunk_count > 1 ? data.narrator.chunk_id + '-' + (data.narrator.chunk_id + data.narrator.chunk_count - 1) : data.narrator.chunk_id}/${data.narrator.total_chunks})`;

                    state.narratorState.book = data.narrator.book;
                    state.narratorState.kb_path = data.narrator.kb_path;
                    state.narratorState.chunk_id = data.narrator.next_chunk_id || (data.narrator.chunk_id + 1);
                    updateModeIndicators(state.loadedAgent, state.loadedCollection, state.narratorMode, state.narratorState.chunk_id);
                    saveSessionStateWithModel(state.loadedModel);
                    saveNarratorProgress(state.sessionId, state.narratorState);
                    updateNextButtonLabel();
                }
                if (metaText) addMetaToMessage(assistantDiv, metaText);

                trackModelUsage(
                    state.loadedModel || elements.modelSelect?.value || data.model,
                    '',
                    data.full_text,
                    data.session_id
                );

                if (data.debug_log && data.debug_log.length > 0) {
                    console.log('%c[RAGSMITH DEBUG]', 'color: #e94560; font-weight: bold');
                    data.debug_log.forEach(line => console.log(line));
                }

                await buildAndSaveMetadata(assistantDiv, data, '', data.session_id || state.sessionId);
                scrollToBottom();
            },
            // onError
            (errorMsg, partialText) => {
                flushPendingRender();
                hideLoading();
                if (ttsController) ttsController.stop();

                if (partialText && assistantDiv) {
                    renderMessageMarkdown(assistantDiv, partialText + '\n\n---\n*⚠️ Response interrupted: ' + errorMsg + '*');
                } else if (partialText) {
                    assistantDiv = addMessage('assistant', '', { header: assistantName, extracting: true });
                    renderMessageMarkdown(assistantDiv, partialText + '\n\n---\n*⚠️ Response interrupted: ' + errorMsg + '*');
                } else {
                    const errorDiv = addMessage('assistant', `⚠️ Error: ${errorMsg}`, { header: assistantName });
                    errorDiv.style.color = '#f87171';
                }

                state.isStreaming = false;
                setStreamingState(false);
                scrollToBottom();
            }
        );
    } catch (e) {
        flushPendingRender();
        hideLoading();
        if (ttsController) ttsController.stop();
        if (resolveAutoplayDone) resolveAutoplayDone();
        addMessage('assistant', `Error: ${e.message}`, { header: assistantName }).style.color = '#f87171';
    }

    // For autoplay: wait until TTS preload + render is done before hiding spinner
    if (autoplayDone) await autoplayDone;
    hideLoading();
    state.isStreaming = false;
    setStreamingState(false);
}

/**
 * Add a speaker/mute icon to an initial prompt message.
 * @param {HTMLElement} messageDiv - The message element
 * @param {string} text - Full message text for on-demand TTS
 * @param {object|null} ttsCtrl - Active TTS controller (autoplay), or null (on-demand)
 * @param {boolean} startMuted - Whether to start in muted state
 */
function _addSpeakerIcon(messageDiv, text, ttsCtrl, startMuted) {
    let muted = startMuted;
    let controller = ttsCtrl;

    const btn = document.createElement('button');
    btn.className = `initial-prompt-speaker ${muted ? 'muted' : 'playing'}`;
    btn.title = muted ? 'Play greeting' : 'Mute';
    btn.innerHTML = muted ? _speakerMutedSvg() : _speakerSvg();

    btn.addEventListener('click', () => {
        if (muted) {
            // Unmute / start playback
            if (controller) {
                controller.unmute();
            } else {
                // No active controller — start fresh TTS
                controller = streamTtsPlayback(text);
            }
            muted = false;
            btn.innerHTML = _speakerSvg();
            btn.className = 'initial-prompt-speaker playing';
            btn.title = 'Mute';
        } else {
            // Mute
            if (controller) controller.mute();
            muted = true;
            btn.innerHTML = _speakerMutedSvg();
            btn.className = 'initial-prompt-speaker muted';
            btn.title = 'Play greeting';
        }
    });

    // Insert into message (top-right)
    messageDiv.style.position = 'relative';
    messageDiv.appendChild(btn);
}

function _speakerSvg() {
    return '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/></svg>';
}

function _speakerMutedSvg() {
    return '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>';
}

/**
 * Load an agent
 */
export async function loadAgentHandler() {
    // Use state.currentAgent (from modal selection) first, fall back to dropdown
    const agent = state.currentAgent || elements.agentSelect.value;
    if (!agent) {
        addSystemMessage('Select an agent first.');
        return;
    }

    // Get currently selected model to warm it during load
    // Prefer selectedModel (from Model Manager) over loadedModel (already in VRAM)
    const selectedModel = state.selectedModel || state.loadedModel || elements.modelSelect?.value || null;

    const menuElements = getMenuElements();
    if (menuElements.loadBtn) {
        menuElements.loadBtn.textContent = 'Loading...';
        menuElements.loadBtn.disabled = true;
    }
    showLoadingOverlay(agent);

    try {
        const finalData = await api.loadAgent(agent, updateLoadingProgress, selectedModel);

        hideLoadingOverlay();

        if (finalData) {
            // Start fresh chat session
            state.sessionId = null;
            clearChat();

            state.loadedAgent = agent;
            state.currentAgent = agent;
            state.loadedCollection = null;  // Clear any loaded collection
            state.loadedCollectionTitle = null;
            state.loadedCollectionDescription = null;
            state.currentCollection = '';
            state.agentSettings = finalData.settings;
            state.agentKbCount = finalData.kb_count || 0;
            state.isLightAgent = finalData.is_light || false;  // Light agents skip citations

            // Populate tool toggles in plus menu
            window.populateToolToggles?.(finalData.settings?.tools || [], finalData.settings);

            // Apply agent-level slider overrides to admin panel
            applyAgentSliderOverrides(finalData.settings);

            // Check if agent has Tier 1 tools (requires LLM tool calling)
            // These are python_exec and shell_exec - require tooling-capable models
            const settings = finalData.settings || {};
            const hasTier1Tools = (settings.tool_python || settings.tool_shell) &&
                                  settings.tool_mode === 'llm_assisted';
            setToolingRequired(hasTier1Tools);

            // Update loadedModel and loadedProvider to reflect the model that was just loaded
            if (selectedModel) {
                state.loadedModel = selectedModel;
                // Sync provider from dropdown (populated by /models/all with correct provider data)
                const menuEls = getMenuElements();
                if (menuEls.modelSelect) {
                    for (const opt of menuEls.modelSelect.options) {
                        if (opt.value === selectedModel || opt.dataset?.modelName === selectedModel) {
                            state.loadedProvider = opt.dataset?.provider || 'local';
                            break;
                        }
                    }
                }
                state.selectedModel = null;  // Clear selection since it's now loaded
                state.selectedModelData = null;
            }

            updateModeIndicators(agent, null, state.narratorMode, state.narratorState.chunk_id);
            updateMenuLoadButton();
            updateHeaderStatus();
            updateFidelitySliderState();  // Enable fidelity slider now that we have KB context
            hideCollectionHeader();
            addSystemMessage(`Loaded ${agent} (${finalData.kb_count} KBs)`);

            // Save session state for persistence across refresh
            saveSessionStateWithModel(state.loadedModel);

            // Set up narrator mode if applicable
            if (finalData.settings?.narrator_mode) {
                state.narratorMode = true;
                state.narratorPaused = false;

                // Fetch server-side narrator progress
                let chunkId = 1;
                let book = finalData.settings.narrator_starting_book || null;
                let kbPath = null;
                try {
                    const progress = await api.fetchNarratorProgress(agent);
                    if (progress?.current_position) {
                        chunkId = progress.current_position.chunk_id || 1;
                        kbPath = progress.current_position.kb || null;
                    }
                    if (progress?.current_book) {
                        book = progress.current_book;
                    }
                } catch (e) {
                    console.error('[Chat] Failed to fetch narrator progress:', e);
                }

                state.narratorState = {
                    book: book,
                    kb_path: kbPath,
                    chunk_id: chunkId,
                    chunk_batch: state.narratorState.chunk_batch || 1,
                    narration_length: state.narratorState.narration_length ?? 1
                };
                updateModeIndicators(agent, null, true, chunkId);
                // Update web search button and narrator menu section
                window.updateWebSearchButtonState?.();
                window.updateNarratorMenuSection?.();
                // Update voice button icon for narrator mode
                updateVoiceBtnIcon(true, !!finalData.settings?.narrator_verbatim);
                // Re-save session now that narrator mode is set
                saveSessionStateWithModel(state.loadedModel);
            } else {
                // Not narrator — reset voice icon to mic and clear any lingering dimming
                updateVoiceBtnIcon(false, false);
                clearNarratorHighlight();
            }

            // Check for required fields before initial prompt
            if (finalData.initial_prompt) {
                const openingData = await api.fetchAgentOpening(agent);

                if (openingData.missing_fields?.length > 0) {
                    // Need to collect fields first
                    state.pendingFields = openingData.missing_fields;
                    addMessage('assistant', openingData.message);
                } else {
                    // All fields collected, run initial prompt
                    setTimeout(() => runInitialPrompt(agent, finalData.settings), 100);
                }
            }
        } else {
            updateMenuLoadButton();
        }
    } catch (e) {
        console.error('[loadAgent] Error:', e);
        hideLoadingOverlay();
        updateMenuLoadButton();
        addSystemMessage(`Failed to load: ${e.message}`);
    }
}

/**
 * Load a collection's KBs
 */
export async function loadCollectionHandler(collectionName) {
    if (!collectionName) {
        addSystemMessage('Select a collection first.');
        return;
    }

    // Get the collection title from the dropdown for display
    const collectionTitle = getSelectedCollectionTitle() || collectionName;

    // Get currently selected model to determine local vs API
    // Prefer selectedModel (from Model Manager) over loadedModel (already in VRAM)
    const selectedModel = state.selectedModel || state.loadedModel || elements.modelSelect?.value || null;

    const menuElements = getMenuElements();
    if (menuElements.loadBtn) {
        menuElements.loadBtn.textContent = 'Loading...';
        menuElements.loadBtn.disabled = true;
    }
    showLoadingOverlay(collectionTitle);

    try {
        const finalData = await api.loadCollection(collectionName, updateLoadingProgress, selectedModel);

        hideLoadingOverlay();

        if (finalData) {
            // Start fresh chat session
            state.sessionId = null;
            clearChat();

            state.loadedCollection = collectionName;
            state.loadedCollectionTitle = finalData.title || collectionName;
            state.loadedCollectionDescription = finalData.description || null;
            state.currentCollection = collectionName;
            state.loadedAgent = null;  // Clear any loaded agent
            state.currentAgent = '';
            state.isLightAgent = false;  // Collections are never light
            state.agentSettings = null;  // Clear agent settings
            state.agentKbCount = finalData.kb_count || 0;
            window.populateToolToggles?.([]);  // Clear tool toggles

            // Also clear the agent select dropdown (for sendMessage header logic)
            if (elements.agentSelect) elements.agentSelect.value = '';

            // Collections don't have tool requirements
            setToolingRequired(false);

            // Update loadedModel and loadedProvider to reflect the model that was just loaded
            if (selectedModel) {
                state.loadedModel = selectedModel;
                // Sync provider from dropdown (populated by /models/all with correct provider data)
                const menuEls = getMenuElements();
                if (menuEls.modelSelect) {
                    for (const opt of menuEls.modelSelect.options) {
                        if (opt.value === selectedModel || opt.dataset?.modelName === selectedModel) {
                            state.loadedProvider = opt.dataset?.provider || 'local';
                            break;
                        }
                    }
                }
                state.selectedModel = null;  // Clear selection since it's now loaded
                state.selectedModelData = null;
            }

            updateModeIndicators(null, state.loadedCollectionTitle, state.narratorMode, state.narratorState.chunk_id);
            updateMenuLoadButton();
            updateHeaderStatus();
            updateFidelitySliderState();  // Enable fidelity slider now that we have KB context
            showCollectionHeader(state.loadedCollectionTitle, state.loadedCollectionDescription);
            addSystemMessage(`Loaded ${state.loadedCollectionTitle} (${finalData.kb_count} KBs)`);

            // Save session state for persistence across refresh
            saveSessionStateWithModel(state.loadedModel);
        } else {
            updateMenuLoadButton();
        }
    } catch (e) {
        console.error('[loadCollection] Error:', e);
        hideLoadingOverlay();
        updateMenuLoadButton();
        addSystemMessage(`Failed to load collection: ${e.message}`);
    }
}

/**
 * Wait for any pending fact extraction to complete before sending
 * Shows "Extracting facts..." in the loading spinner and disables input
 * @param {number} maxWaitMs - Maximum time to wait (default 30 seconds)
 * @param {number} pollIntervalMs - How often to check (default 500ms)
 * @returns {Promise<boolean>} - True if extraction completed, false if timed out
 */
async function waitForExtraction(maxWaitMs = 30000, pollIntervalMs = 500) {
    if (!state.sessionId) return true;  // No session yet, nothing to wait for
    if (state.memoryLevel < 2) return true;  // No extraction at Basic or Off levels

    // Check initial status
    const initialStatus = await api.checkExtractionStatus(state.sessionId);
    if (!initialStatus.extracting) return true;

    // Disable input and send button while waiting (like during streaming)
    // Keep the user's typed message visible but greyed out
    elements.messageInput.disabled = true;
    elements.sendBtn.disabled = true;

    // Show loading spinner with detailed extraction message
    showLoading();
    updateLoadingText('Message queued, waiting for fact extraction...');

    const startTime = Date.now();

    // Poll until extraction completes or timeout
    while (Date.now() - startTime < maxWaitMs) {
        await new Promise(resolve => setTimeout(resolve, pollIntervalMs));

        const status = await api.checkExtractionStatus(state.sessionId);
        if (!status.extracting) {
            hideLoading();
            // Re-enable input and send button
            elements.messageInput.disabled = false;
            elements.sendBtn.disabled = false;
            if (!isVoiceActive()) elements.messageInput.focus();
            return true;
        }
    }

    // Timed out - re-enable anyway so user isn't stuck
    hideLoading();
    elements.messageInput.disabled = false;
    elements.sendBtn.disabled = false;
    if (!isVoiceActive()) elements.messageInput.focus();
    return false;
}

// ─── HITL Tool Approval Modal ───

const toolApprovalConfig = {
    'calculator':   { label: 'Calculator',  icon: '🔢' },
    'python_exec':  { label: 'Python',      icon: '🐍' },
    'shell_exec':   { label: 'Shell',       icon: '💻' },
    'file_ops':     { label: 'File Ops',    icon: '📄' },
    'web_fetch':    { label: 'Web Fetch',   icon: '🌐' },
    'http_request': { label: 'HTTP Request', icon: '🔗' },
    'email_send':   { label: 'Email Send',  icon: '✉️' },
    'web_search':   { label: 'Web Search',  icon: '🔍' },
    'rag_search':   { label: 'Doc Search',  icon: '📚' },
    'datetime':     { label: 'Date/Time',   icon: '🕐' },
    'agent_db':     { label: 'Database',    icon: '🗄️' },
    'kb_embed':     { label: 'KB Embed',   icon: '📥' }
};

/**
 * Handle a tool_approval SSE event by showing the approval modal.
 * @param {Object} approvalData - {approval_id, tool, args, risk, info, step, max_steps}
 * @param {string} agentName - Current agent name (for remember-choice key)
 */
function handleToolApproval(approvalData, agentName) {
    const { approval_id, tool, risk, info } = approvalData;
    const config = toolApprovalConfig[tool] || { label: tool, icon: '🔧' };
    const toolLabel = config.label;

    // Check localStorage for remembered choice
    const rememberKey = `hitl_${agentName}_${tool}_${risk}`;
    const remembered = localStorage.getItem(rememberKey);
    if (remembered) {
        api.postToolApproval(approval_id, remembered);
        if (remembered === 'allow') {
            updateLoadingText(`Auto-allowed ${toolLabel}...`);
        }
        return;
    }

    // Populate modal elements
    const modal = document.getElementById('tool-approval-modal');
    const iconEl = document.getElementById('tool-approval-icon');
    const nameEl = document.getElementById('tool-approval-name');
    const actionEl = document.getElementById('tool-approval-action');
    const previewEl = document.getElementById('tool-approval-preview');
    const rememberCheckbox = document.getElementById('tool-approval-remember');
    const rememberName = document.getElementById('tool-approval-remember-name');

    // Set icon with risk-colored background
    iconEl.textContent = config.icon;
    iconEl.className = `tool-approval-icon risk-${risk}`;

    // Set tool name with risk badge
    const riskLabels = { write: 'Write', code: 'Code', external: 'External', dangerous: 'Danger' };
    nameEl.innerHTML = `${escapeHtml(toolLabel)} <span class="tool-approval-risk-badge risk-${risk}">${riskLabels[risk] || risk}</span>`;

    // Set action description
    actionEl.textContent = info?.action || `Use ${tool}`;

    // Set content preview
    if (info?.preview) {
        previewEl.textContent = info.preview;
        previewEl.classList.remove('hidden');
    } else {
        previewEl.classList.add('hidden');
    }

    // Remember checkbox
    rememberName.textContent = toolLabel;
    rememberCheckbox.checked = false;

    // Wire up buttons (replace onclick to avoid stacking)
    const allowBtn = document.getElementById('tool-approval-allow');
    const denyBtn = document.getElementById('tool-approval-deny');
    const closeBtn = document.getElementById('tool-approval-close');

    const cleanup = () => {
        modal.classList.remove('active');
        modal.classList.add('hidden');
    };

    const handleAllow = () => {
        if (rememberCheckbox.checked) {
            localStorage.setItem(rememberKey, 'allow');
        }
        api.postToolApproval(approval_id, 'allow');
        cleanup();
        updateLoadingText(`Running ${toolLabel}...`);
    };

    const handleDeny = () => {
        if (rememberCheckbox.checked) {
            localStorage.setItem(rememberKey, 'deny');
        }
        api.postToolApproval(approval_id, 'deny');
        cleanup();
        updateLoadingText(`Denied ${toolLabel}, continuing...`);
    };

    allowBtn.onclick = handleAllow;
    denyBtn.onclick = handleDeny;
    if (closeBtn) closeBtn.onclick = handleDeny;

    // Show modal
    modal.classList.remove('hidden');
    modal.classList.add('active');
}

/**
 * Send a chat message
 */
/**
 * Save a typed message as a dictation entry instead of routing through
 * /chat/agent. Mirrors what voice.js does after a voice-dictation recording
 * finishes — same /dictation endpoint, same UI styling. The only difference
 * is the text source is the typed input rather than the STT transcript.
 *
 * Used by sendMessage() when state.dictationMode is on. No LLM is invoked.
 */
async function sendAsDictation(text) {
    elements.messageInput.value = '';

    // Add a placeholder message immediately so the UI doesn't appear frozen
    // while the POST is in flight. We use extracting:true only when
    // dictationExtractFacts is on, matching the voice-dictation behavior.
    const msgEl = addMessage('user', text, {
        header: 'You',
        isDictation: true,
        extracting: !!state.dictationExtractFacts,
    });

    try {
        const res = await fetch('/dictation', {
            method: 'POST',
            headers: api.getHeaders(true),
            body: JSON.stringify({
                text,
                session_id: state.sessionId || null,
                agent: state.loadedAgent || null,
                collection: state.loadedCollection || null,
                extract_facts: !!state.dictationExtractFacts,
                model: state.loadedModel || null,
            }),
        });
        if (!res.ok) throw new Error(`save failed (${res.status})`);
        const data = await res.json();

        if (data.session_id && !state.sessionId) {
            state.sessionId = data.session_id;
        }

        // Replace the placeholder with a fully-wired version that has the
        // real messageId — addMessage attaches the action bar (delete + redo
        // + TTS) at construction time, so we have to rebuild rather than
        // mutate. This mirrors how voice.js does it.
        if (msgEl && data.message_id) {
            const parent = msgEl.parentNode;
            const next = msgEl.nextSibling;
            msgEl.remove();
            const replacement = addMessage('user', text, {
                isDictation: true,
                messageId: data.message_id,
                extracting: !!data.extracting,
            });
            if (replacement && parent && next && replacement.parentNode === parent
                && next !== replacement) {
                parent.insertBefore(replacement, next);
            }

            if (data.extracting && data.session_id) {
                const { pollDictationExtractionStatus } = await import('./voice.js');
                pollDictationExtractionStatus(data.session_id, replacement);
            }
        }
    } catch (e) {
        console.error('[Dictation] Save failed:', e);
        if (msgEl) msgEl.remove();
        addMessage('system', `Dictation save failed: ${e.message}`);
    }
}

export async function sendMessage(presetMessage = null) {
    // presetMessage (voice transcripts) bypasses the input box entirely, so
    // the transcript never flashes through the textarea on its way to chat.
    let message = presetMessage !== null ? presetMessage.trim() : elements.messageInput.value.trim();
    if (state.isStreaming) return;
    resetToolCallPitch();

    // Allow an empty message when:
    //  - narrator mode (means "continue"), or
    //  - there's an image attached AND vision is available (agent vision tool or a
    //    multimodal model) — a promptless image submit gets described by the vision model.
    // A video still uploading has no media_id yet — sending now would
    // reference bytes the server does not have.
    if (hasPendingUploads()) {
        addMessage('system', 'Still uploading — one moment.');
        return;
    }

    const attachedNow = getAttachments();
    const hasImageAttachment = attachedNow.some(a => a.type === 'image');
    const hasVideoAttachment = attachedNow.some(a => a.type === 'video');
    const visionAvailable =
        !!(state.agentSettings && state.agentSettings.tool_vision) || loadedModelSupportsVision();
    // A promptless video always has something to say: even with no vision at
    // all its audio gets transcribed, so it never hits the "nothing to send"
    // case the way a promptless image can.
    // Deliberately NOT gated on visionAvailable any more. Storing an image and
    // understanding it are separate capabilities: the attachment belongs in the
    // conversation whether or not anything can read it today, and the server
    // answers accordingly — it acknowledges the file, refuses to guess at its
    // contents, and leaves the turn redoable once something with eyes is
    // available. Blocking the send here threw the image away instead, and the
    // check could not see the whole picture anyway: it knows only about this
    // agent's vision tool, not about a multimodal worker or a cloud lane.
    const imageOnlyVisionSubmit = !message && hasImageAttachment;
    if (
        !message &&
        !imageOnlyVisionSubmit &&
        !hasVideoAttachment &&
        !state.narratorMode &&
        state.pendingFields.length === 0
    ) {
        return;
    }

    // Wait for any pending fact extraction to complete before sending
    // This prevents concurrent LLM calls which can cause slowness on GPU
    await waitForExtraction();

    // Check for slash commands
    if (message.startsWith('/')) {
        elements.messageInput.value = '';
        await handleCommand(message);
        return;
    }

    // Dictation mode — save the typed text as a dictation entry instead of
    // sending it through the normal chat pipeline. No LLM, no agent, no
    // tool loop. The /dictation endpoint already handles everything; we
    // just need to route to it. This is a top-level early branch so the
    // pending-fields, instruction-trigger, and other agent paths below are
    // intentionally skipped — dictation is a "save what I just typed"
    // operation, not a chat turn.
    if (state.dictationMode && message) {
        await sendAsDictation(message);
        return;
    }

    // Handle pending field collection (required fields before initial prompt)
    if (state.pendingFields.length > 0 && message) {
        const currentField = state.pendingFields[0];
        addMessage('user', message, { header: 'You' });
        elements.messageInput.value = '';

        // Store the field value
        await api.storeFieldValue(state.currentAgent, currentField.key, message);

        state.pendingFields = state.pendingFields.slice(1);

        if (state.pendingFields.length > 0) {
            // Ask for next field
            const nextField = state.pendingFields[0];
            const assistantName = getAssistantName(state.currentAgent);
            addMessage('assistant', `Got it! And what is your ${nextField.label.toLowerCase()}?`, { header: assistantName });
        } else {
            // All fields collected - run initial prompt
            const assistantName = getAssistantName(state.currentAgent);
            addMessage('assistant', 'Thanks! Let me get started...', { header: assistantName });
            setTimeout(() => runInitialPrompt(state.currentAgent, state.agentSettings), 100);
        }
        return;
    }

    // Handle pending instruction field response
    if (state.pendingInstruction && message && state.loadedAgent) {
        addMessage('user', message, { header: 'You' });
        elements.messageInput.value = '';
        showLoading();

        const assistantName = getAssistantName(state.loadedAgent);

        try {
            const result = await api.submitInstructionResponse(
                state.loadedAgent,
                state.pendingInstruction.key,
                message,
                state.sessionId
            );
            hideLoading();

            if (result.success) {
                addMessage('assistant', result.confirmation, { header: assistantName });
            } else {
                addMessage('assistant', `Could not process: ${result.error || 'Unknown error'}`, { header: assistantName });
            }
        } catch (e) {
            hideLoading();
            addMessage('assistant', `Error: ${e.message}`, { header: assistantName });
        }

        state.pendingInstruction = null;
        return;
    }

    // Check if message is an instruction field trigger (agent only, not for collections)
    if (state.loadedAgent && message && (!state.narratorMode || state.narratorPaused)) {
        try {
            const triggerCheck = await api.checkTrigger(state.loadedAgent, message);

            if (triggerCheck.is_trigger) {
                // Show user's trigger word
                addMessage('user', message, { header: 'You' });
                elements.messageInput.value = '';

                // Show instructions immediately (no LLM)
                const inst = triggerCheck.instruction_field;
                const assistantName = getAssistantName(state.loadedAgent);
                addMessage('assistant', inst.instructions, { header: assistantName });

                // Set pending state for next message
                state.pendingInstruction = inst;
                return;
            }
        } catch (e) {
            console.error('[Trigger check] Error:', e);
            // Continue to normal chat on error
        }
    }

    // In narrator mode, empty message means "continue" (don't show in chat)
    // In verbatim mode, ALL messages are treated as continue (no user interaction)
    const isVerbatimNarrator = state.narratorMode && !state.narratorPaused && state.agentSettings?.narrator_verbatim;
    const isBlankContinue = state.narratorMode && !state.narratorPaused && (!message || isVerbatimNarrator);
    if (isBlankContinue) {
        message = 'continue';
    }

    // Validate that a model is loaded before sending
    if (!state.loadedModel) {
        addSystemMessage('No model loaded. Open settings and load a model first.');
        return;
    }

    // Check for rate limit cooldown
    const cooldownMs = getCooldownRemaining();
    if (cooldownMs > 0) {
        const secs = Math.ceil(cooldownMs / 1000);
        addSystemMessage(`Rate limit cooldown active. Please wait ${secs} seconds before sending another message.`);
        return;
    }

    // Get any attachments before sending
    const attachmentList = getAttachments();
    const hasAttachments = attachmentList.length > 0;

    // Prepare attachments data for API (stored separately in DB)
    // Server will format attachments + message for LLM context
    const attachmentsForApi = hasAttachments
        ? attachmentList.map(att => ({
            id: att.id,
            type: att.type,
            name: att.name,
            // An uploaded image keeps its base64 locally for the composer
            // preview, but sends only the reference: the server reads the
            // bytes from wherever the container's storage put them. Sending
            // both would push the image back into the message JSON and undo
            // the point of uploading it.
            content: att.media_id && att.type === 'image' ? '' : att.content,
            media_id: att.media_id || undefined,
            meta: att.meta || undefined,
            size: att.size,
            media_type: att.media_type || undefined
        }))
        : null;

    lastDispatchedTurn = { message, attachments: attachmentsForApi };

    const agent = state.loadedAgent || elements.agentSelect.value;
    const collection = state.loadedCollection || null;
    let model = state.loadedModel || undefined;  // Only use loadedModel - never fallback to dropdown
    // If bypass is active on a :free model, strip suffix to use OpenRouter balance
    if (model && model.endsWith(':free') && isBypassed(model)) {
        model = model.replace(':free', '');
    }
    const userQuestion = message; // Save for citation request (original user text)

    // Don't show "continue" in chat - just like CLI clears the line
    let userMessageEl = null;
    if (!isBlankContinue) {
        // Show user message with attachment chips if applicable
        userMessageEl = addMessage('user', message, {
            header: 'You',
            render: true,
            attachments: hasAttachments ? attachmentList : undefined
        });
    }
    elements.messageInput.value = '';
    elements.messageInput.style.height = 'auto';

    // Store for potential retry (before clearing attachments)
    setLastSentMessage(message, attachmentList, userMessageEl);

    // Clear attachments after capturing
    if (hasAttachments) {
        clearAttachments();
    }
    state.isStreaming = true;
    setStreamingState(true);

    // Show loading spinner. (Stop wiring is no longer per-request — the send
    // button is the stop button now, wired once at app init.)
    showLoading();

    let assistantDiv = null;
    let fullText = '';
    let thinkingShown = false;  // Track if we've shown the thinking section
    let voicedResponseLen = 0;  // Track how much response text has been sent to TTS
    let toolsUsed = {};  // Track tools executed during this response {name: count}
    let stepLog = [];  // Structured step log for timeline
    let pendingDataBlocks = [];  // Raw output data blocks from management tools
    let inToolLoop = false;  // True once tool_progress events arrive (text+tools interleaved)
    let pendingDocLink = null;  // Doc create link stored here, applied after final render
    let pendingImageResults = null;  // Web image search results for thumbnail strip
    let thinkingText = '';  // Accumulated inter-step reasoning from tool loops
    // Use collection title (human-friendly) for display, not folder name
    const assistantName = getAssistantName(agent || state.loadedCollectionTitle || collection);

    try {
        await api.sendChatMessage(
            {
                message: message,  // Raw message - server formats with attachments for LLM
                sessionId: state.sessionId,
                model,
                agent: agent || undefined,
                collection: collection || undefined,
                temperature: state.temperature,
                fidelity: state.fidelity,
                memoryLevel: state.memoryLevel,
                kbThreshold: state.agentSettings?.agent_kb_threshold ?? undefined,
                debugMode: state.debugMode,
                webSearchEnabled: state.webSearchEnabled,
                webSearchDepth: state.webSearchDepth,
                webSearchImages: state.imageSearchEnabled && state.webSearchEnabled,
                narratorMode: state.narratorMode,
                narratorState: state.narratorState,
                disabledTools: [...state.disabledTools],
                attachments: attachmentsForApi
            },
            // onChunk - throttled markdown rendering for smooth output
            // null = retract signal (speculatively-streamed text was inter-step reasoning)
            (chunk) => {
                // Retract: clear text that was streamed during the tool loop
                // but turned out to be inter-step reasoning, not final response.
                if (chunk === null) {
                    fullText = '';
                    voicedResponseLen = 0;
                    if (assistantDiv) {
                        const contentDiv = assistantDiv.querySelector('.message-content');
                        if (contentDiv) contentDiv.innerHTML = '';
                    }
                    // Discard any voice TTS chunks that were buffered/sent for
                    // the now-retracted text. Without this, voice reads what
                    // was originally classified as the response but is actually
                    // the model's inter-step thinking.
                    if (isVoiceActive()) onVoiceRetract();
                    return;
                }
                // Finalize thinking dropdown when response text starts streaming
                if (thinkingText && !thinkingShown) {
                    // thinkingText was set but thinkingShown wasn't — shouldn't happen, but guard
                } else if (thinkingText && thinkingShown) {
                    addThinkingToMessage(assistantDiv, thinkingText, true);
                }
                if (!assistantDiv) {
                    if (!inToolLoop) {
                        hideLoading();
                    }
                    // In tool loop: spinner stays in place, message appears below it
                    //
                    // extracting:true is also what BUILDS this footer, so it
                    // stays true even when nothing will be extracted. Whether
                    // the status row appears is decided in addMessage; passing
                    // false here took the play, redo, edit, copy, export and
                    // delete buttons with it.
                    assistantDiv = addMessage('assistant', '', { header: assistantName, extracting: true, running: inToolLoop });
                    if (state.narratorMode) highlightNarratorMessage(assistantDiv);
                }
                fullText += chunk;

                // Handle reasoning model thinking content
                if (hasThinkingContent(fullText)) {
                    const { thinking, response, isComplete } = separateThinkingContent(fullText);

                    if (thinking) {
                        addThinkingToMessage(assistantDiv, thinking, isComplete);
                        thinkingShown = true;
                    }

                    // Only render the response part (not thinking)
                    if (response) {
                        throttledRender(assistantDiv, response);
                        // Feed response delta (not thinking) to voice TTS.
                        // Speculative inter-step text may still be voiced here,
                        // but onVoiceRetract() halts and flushes in-flight audio
                        // the instant the server retracts it, so a leak is cut
                        // short rather than read as a full paragraph.
                        if (isVoiceActive() && isComplete) {
                            const delta = response.slice(voicedResponseLen);
                            if (delta) onVoiceStreamChunk(delta);
                            voicedResponseLen = response.length;
                        }
                    }
                } else {
                    // No thinking tags - render everything
                    throttledRender(assistantDiv, fullText);
                    if (isVoiceActive()) onVoiceStreamChunk(chunk);
                }
            },
            // onComplete
            (data) => {
                // Clean up any pending throttled render
                flushPendingRender();

                if (!assistantDiv) {
                    hideLoading();
                    if (data.cancelled) {
                        assistantDiv = addMessage('assistant', '', { header: assistantName, cancelled: true, hasSession: !!(data.session_id || state.sessionId) });
                    } else {
                        assistantDiv = addMessage('assistant', '', { header: assistantName, extracting: true });
                    }
                }

                // Handle final render for reasoning models
                let finalContent = data.full_text;
                if (hasThinkingContent(data.full_text)) {
                    const { thinking, response, isComplete } = separateThinkingContent(data.full_text);

                    if (thinking) {
                        // Server-measured duration wins over any client guess
                        addThinkingToMessage(assistantDiv, thinking, true, data.thinking_ms ?? null);
                    }
                    finalContent = response;
                }

                // Strip doc_create download link from markdown before rendering
                // (we insert a styled HTML element after render instead)
                if (pendingDocLink) {
                    // Remove the appended ---\n[filename](url) from full_text
                    const linkPattern = /\n\n---\n\[[^\]]+\]\(\/documents\/[^)]+\)$/;
                    finalContent = finalContent.replace(linkPattern, '');
                }

                // For cancelled responses, swap in "Response stopped." (empty
                // bubble) or append a "— Stopped —" suffix (partial text).
                if (data.cancelled) {
                    finalContent = _decorateCancelledContent(finalContent);
                    markMessageCancelled(assistantDiv, !!(data.session_id || state.sessionId), data.message_id);
                }

                // Final markdown render
                renderMessageMarkdown(assistantDiv, finalContent);

                // Insert doc_create download link in message footer (not inside message-content,
                // which can be very tall with tables and push the link out of view)
                // Uses fetch + blob to include auth header (plain <a> clicks don't send X-Auth-Token)
                if (pendingDocLink && assistantDiv) {
                    const actionBar = assistantDiv.querySelector('.message-actions');
                    const ext = pendingDocLink.filename.split('.').pop()?.toUpperCase() || 'FILE';
                    const linkEl = document.createElement('div');
                    linkEl.className = 'doc-download-bar';
                    const btn = document.createElement('button');
                    btn.className = 'doc-download-link';
                    btn.innerHTML = `<span class="doc-download-icon">&#8681;</span> ${escapeHtml(pendingDocLink.filename)}<span class="doc-download-badge">${escapeHtml(ext)}</span>`;
                    btn.addEventListener('click', async () => {
                        try {
                            // getHeaders carries the container and capsule
                            // tokens too; with only X-Auth-Token this download
                            // came back 401 for anyone inside a container.
                            const { getHeaders } = await import('./api.js');
                            const res = await fetch(pendingDocLink.url, { headers: getHeaders(false) });
                            if (!res.ok) throw new Error(`Download failed: ${res.status}`);
                            const blob = await res.blob();
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = pendingDocLink.filename;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            URL.revokeObjectURL(url);
                        } catch (e) {
                            btn.classList.add('expired');
                            btn.innerHTML = `<span class="doc-download-icon">&#8681;</span> ${escapeHtml(pendingDocLink.filename)}<span class="doc-download-badge expired-badge">EXPIRED</span>`;
                            btn.disabled = true;
                        }
                    });
                    linkEl.appendChild(btn);
                    if (actionBar) {
                        actionBar.parentNode.insertBefore(linkEl, actionBar);
                    } else {
                        assistantDiv.appendChild(linkEl);
                    }
                }

                if (data.session_id) {
                    state.sessionId = data.session_id;
                    // Save session state when we get a session ID
                    saveSessionStateWithModel(model);
                }

                // Update narrator state from response
                // Add meta info (without agent name since it's in header now)
                let metaText = '';
                // Only show KB Top when agent/collection has more than 3 KBs
                if (data.kb_top && (agent || collection) && state.agentKbCount > 3) metaText += `KB Top: ${data.kb_top}`;
                // Skip model name for verbatim narrator responses (no LLM used)
                if (data.model && !data.verbatim) metaText += (metaText ? ' | ' : '') + `Model: ${data.model}`;
                if (data.narrator) {
                    // Set DOM attributes FIRST so badge count picks them up
                    if (assistantDiv) {
                        assistantDiv.dataset.narratorChunkId = data.narrator.chunk_id;
                        assistantDiv.dataset.narratorTotalChunks = data.narrator.total_chunks;
                        assistantDiv.dataset.narratorSection = data.narrator.section;
                        assistantDiv.dataset.narratorBook = data.narrator.book || '';
                        assistantDiv.dataset.narratorKbPath = data.narrator.kb_path || '';
                        assistantDiv.dataset.narratorChunkCount = data.narrator.chunk_count || 1;
                    }
                    metaText += (metaText ? ' | ' : '') + `${data.narrator.section} (${data.narrator.chunk_count > 1 ? data.narrator.chunk_id + '-' + (data.narrator.chunk_id + data.narrator.chunk_count - 1) : data.narrator.chunk_id}/${data.narrator.total_chunks})`;

                    state.narratorState.book = data.narrator.book;
                    state.narratorState.kb_path = data.narrator.kb_path;
                    state.narratorState.chunk_id = data.narrator.next_chunk_id || (data.narrator.chunk_id + 1);
                    updateModeIndicators(state.loadedAgent, state.loadedCollection, state.narratorMode, state.narratorState.chunk_id);
                    saveSessionStateWithModel(model);
                    saveNarratorProgress(state.sessionId, state.narratorState);
                    updateNextButtonLabel();
                }
                if (metaText) {
                    addMetaToMessage(assistantDiv, metaText);
                }

                // Show debug log in console if present
                if (data.debug_log && data.debug_log.length > 0) {
                    console.log('%c[RAGSMITH DEBUG]', 'color: #e94560; font-weight: bold');
                    data.debug_log.forEach(line => console.log(line));
                }

                // Show related topics from routing matches (no LLM call needed)
                // Skip for light agents (single KB) and narrator mode
                if ((agent || collection) && (!state.narratorMode || state.narratorPaused) && !state.isLightAgent) {
                    if (data.routing_matches && data.routing_matches.length > 0) {
                        showRoutingMatches(assistantDiv, data.routing_matches);
                    }
                }

                // Show tools used badges in message footer
                if (Object.keys(toolsUsed).length > 0) {
                    showToolsUsed(assistantDiv, toolsUsed);
                }

                // Render web image search results if available
                if (pendingImageResults && pendingImageResults.length > 0 && assistantDiv) {
                    showImageSearchResults(assistantDiv, pendingImageResults);
                    pendingImageResults = null;
                }

                // Solidify loading steps into permanent accordion (replaces hideLoading + turn summary + step timeline)
                const finalStepLog = data.step_log || (stepLog.length > 0 ? stepLog : null);
                solidifyLoadingSteps(assistantDiv, finalStepLog);

                // Render verbatim data blocks from management tools
                if (pendingDataBlocks.length > 0 && assistantDiv) {
                    renderDataBlocks(assistantDiv, pendingDataBlocks);
                }

                // Hide stop button
                showStopButton(false);

                // Track model usage (async, non-blocking)
                // If tool_loop provided real tokens via server-side recording, skip frontend estimation
                const hasToolTokens = data.token_usage && data.token_usage.total_tokens > 0;
                trackModelUsage(
                    model || data.model,
                    message,
                    data.full_text,
                    data.session_id,
                    hasToolTokens ? 'tool_loop' : 'chat',
                    data.token_usage || null
                );

                // Poll for extraction completion to enable delete button
                // Skip for cancelled - no extraction ran
                if (!data.cancelled && data.session_id && assistantDiv) {
                    // If footer was showing "Running...", switch to "Extracting facts..."
                    const statusSpan = assistantDiv.querySelector('.extraction-status span');
                    if (statusSpan && statusSpan.textContent === 'Running...') {
                        statusSpan.textContent = 'Extracting facts...';
                    }
                    pollExtractionStatus(data.session_id, assistantDiv);
                } else if (data.cancelled && assistantDiv) {
                    // Remove "Running..." spinner on cancel
                    const runStatus = assistantDiv.querySelector('.extraction-status');
                    if (runStatus) runStatus.remove();
                }

                // Fetch and display citations for agent/collection chats
                // Covers: pre-flight SearchDecision, tool loop rag_search calls, automatic KB search
                // Skip narrator mode (has its own source tracking)
                if ((agent || collection) && (!state.narratorMode || state.narratorPaused)) {
                    api.fetchCitations({
                        agent,
                        collection,
                        question: message,
                        response: data.full_text,
                        sessionId: data.session_id
                    }).then(citations => {
                        if (citations && citations.length > 0) {
                            updateCitations(assistantDiv, citations);
                        }
                    }).catch(() => {});
                }

                // Success - reset failure counter and clear retry state
                recordSuccess();
                clearLastSentMessage();
                clearCooldownUI();

                // Trigger TTS if voice mode is active
                if (isVoiceActive()) {
                    // Verbatim narrator uses HTTP TTS (no WebSocket/mic)
                    // Non-verbatim narrator and normal voice use WebSocket progressive TTS
                    const isVerbatimNarrator = state.narratorMode && !state.narratorPaused && state.agentSettings?.narrator_verbatim;
                    if (isVoiceNarratorActive() && isVerbatimNarrator) {
                        autoPlayNarratorTts(data.full_text);
                    } else {
                        onVoiceResponseComplete(data.full_text);
                    }
                }

                // Build and save per-message analytics (skip for verbatim - no LLM used)
                if (!data.verbatim) {
                    buildAndSaveMetadata(assistantDiv, data, message, data.session_id || state.sessionId);
                }

                scrollToBottom();
            },
            // onError - graceful handling of streaming errors
            async (errorMsg, partialText, errorSessionId, busy = false) => {
                flushPendingRender();
                showStopButton(false);
                hideLoading();

                // Admission control: all workers busy → queue overlay + auto-retry,
                // rather than an inline error the user has to manually retry.
                if (busy) {
                    if (errorSessionId && !state.sessionId) state.sessionId = errorSessionId;
                    enterBusyQueue();
                    return;
                }

                // Capture session ID from error so retries use the same conversation
                if (errorSessionId && !state.sessionId) {
                    state.sessionId = errorSessionId;
                }

                // Record failure and check for cooldown
                const cooldownTriggered = recordFailure();
                if (cooldownTriggered) {
                    startCooldownUI(COOLDOWN_MS);  // 60 second cooldown
                }

                // Construct the error message content for saving
                let errorContent;
                if (partialText) {
                    errorContent = partialText + '\n\n---\n*⚠️ Response interrupted: ' + errorMsg + '*';
                } else {
                    errorContent = `⚠️ Error: ${errorMsg}`;
                }

                // Save error message to database for retry persistence and get message ID
                let savedMessageId = null;
                if (state.sessionId) {
                    try {
                        const result = await api.saveErrorMessage(state.sessionId, errorContent, state.loadedModel, lastDispatchedTurn);
                        savedMessageId = result.message_id || null;
                    } catch {
                    }
                }

                // If we got partial text, show it with an error indicator
                if (partialText && assistantDiv) {
                    if (savedMessageId) assistantDiv.dataset.messageId = savedMessageId;
                    renderMessageMarkdown(assistantDiv, errorContent);
                    addRetryButton(assistantDiv, cooldownTriggered, savedMessageId);
                } else if (partialText) {
                    assistantDiv = addMessage('assistant', '', { header: assistantName });
                    if (savedMessageId) assistantDiv.dataset.messageId = savedMessageId;
                    renderMessageMarkdown(assistantDiv, errorContent);
                    addRetryButton(assistantDiv, cooldownTriggered, savedMessageId);
                } else {
                    // No partial text - just show the error with retry button
                    const errorDiv = addMessage('assistant', errorContent, { header: assistantName });
                    if (savedMessageId) errorDiv.dataset.messageId = savedMessageId;
                    errorDiv.style.color = '#f87171';
                    addRetryButton(errorDiv, cooldownTriggered, savedMessageId);
                }

                state.isStreaming = false;
                setStreamingState(false);

                // Resume voice listening on error
                if (isVoiceActive()) {
                    onVoiceResponseComplete('');
                }

                scrollToBottom();
            },
            // onSearchStatus - web/doc search progress updates (depth-aware)
            (searchData) => {
                const depthLabels = {
                    'surface': 'Searching',
                    'shallow': 'Deep searching',
                    'deep': 'Researching'
                };
                if (searchData.status === 'deciding') {
                    // Search decision service is analyzing the request
                    updateLoadingText('Analyzing request...');
                } else if (searchData.status === 'searching') {
                    const label = depthLabels[searchData.depth] || 'Searching';
                    showStopButton(true);
                    exitBusyQueueIfOpen();
                    _wireStopButton();
                    updateLoadingText(`${label}...`);
                    onVoiceToolEvent('search');
                } else if (searchData.status === 'cancelled') {
                    showStopButton(false);
                    updateLoadingText('Stopping...');
                } else if (searchData.status === 'searching_docs') {
                    // RAG search tool is searching knowledge base
                    const query = searchData.query;
                    showStopButton(true);
                    exitBusyQueueIfOpen();
                    _wireStopButton();
                    updateLoadingText(query ? `Searching docs: "${query}"...` : 'Searching knowledge base...');
                } else if (searchData.status === 'docs_complete') {
                    // RAG search complete
                    const count = searchData.result_count || 0;
                    const searchQuery = searchData.search_query;
                    if (count === 0) {
                        updateLoadingText(searchQuery
                            ? `"${searchQuery}" → No docs found. Generating...`
                            : 'No docs found. Generating...');
                    } else {
                        updateLoadingText(searchQuery
                            ? `"${searchQuery}" → ${count} docs. Generating...`
                            : `Found ${count} docs. Generating...`);
                    }
                } else if (searchData.status === 'complete') {
                    const count = searchData.result_count || 0;
                    const fetched = searchData.fetched_count || 0;
                    const hasSummary = searchData.has_summary;
                    const searchQuery = searchData.search_query;

                    // Show search results - be explicit when no sources found
                    let statusText;
                    if (count === 0) {
                        // No results found - warn user
                        statusText = searchQuery
                            ? `"${searchQuery}" → No sources found. Generating...`
                            : 'No sources found. Generating...';
                    } else if (searchData.depth === 'deep' && hasSummary) {
                        statusText = `Analyzed ${fetched} sources. Generating...`;
                    } else if (searchData.depth === 'shallow' && fetched > 0) {
                        statusText = `Read ${fetched} pages. Generating...`;
                    } else {
                        statusText = searchQuery
                            ? `"${searchQuery}" → ${count} results. Generating...`
                            : `Found ${count} results. Generating...`;
                    }
                    updateLoadingText(statusText);
                    // Capture image results for rendering after response completes
                    if (searchData.image_results && searchData.image_results.length > 0) {
                        pendingImageResults = searchData.image_results;
                    }
                } else if (searchData.status === 'error') {
                    updateLoadingText('Search failed. Generating...');
                }
            },
            // onToolStatus - tool execution progress updates (supports multi-step)
            (toolData) => {
                const step = toolData.step;
                const maxSteps = toolData.max_steps;
                const stepPrefix = step && step > 1 ? `Step ${step}: ` : '';

                // Mark that we're in a tool loop (spinner stays visible alongside text)
                inToolLoop = true;

                // Flush active TTS session before tool execution pauses the text stream
                if (isVoiceActive() && (toolData.status === 'planning' || toolData.status === 'found_tool')) {
                    onVoiceToolInterrupt();
                }

                // Handle progress events from tool loop
                if (toolData.status === 'planning') {
                    updateLoadingText(toolData.message || 'Planning approach...');
                    // Show plan text in collapsible thinking section (collapsed by default)
                    if (toolData.plan_text) {
                        if (!assistantDiv) {
                            // Don't recreate the loading indicator — addMessage
                            // appends to the end of the chat container, so the
                            // existing loader naturally ends up above the new
                            // message. Recreating it would put the loader below
                            // the assistant message, which looks wrong.
                            assistantDiv = addMessage('assistant', '', { header: assistantName, extracting: true, running: true });
                        }
                        addThinkingToMessage(assistantDiv, toolData.plan_text, true);
                        thinkingShown = true;
                    }
                } else if (toolData.status === 'tool_error') {
                    // A hard provider limit used to reach the user as "it just
                    // didn't call any tools". Say what happened, and make it
                    // stick around longer than a spinner caption would.
                    updateLoadingText(toolData.message || 'Tools unavailable — answering without them...');
                    showToast(toolData.message || 'Tools unavailable for this turn', 'error', 9000);
                } else if (toolData.status === 'thinking') {
                    updateLoadingText(toolData.message || `${stepPrefix}Deciding tool...`);
                } else if (toolData.status === 'found_tool') {
                    // Re-show spinner if text streaming already hid it (e.g. planning mode)
                    if (!document.getElementById('loading-indicator')) showLoading();
                    showStopButton(true);
                    exitBusyQueueIfOpen();
                    _wireStopButton();
                    updateLoadingText(toolData.message || `${stepPrefix}Using ${toolData.tool}...`);
                    // Chime on tool START so the user gets immediate feedback when a
                    // tool is dispatched. Previously the chime fired on tool_complete,
                    // which made parallel tool calls all chime in a burst at the END
                    // of execution — confusing because the start was silent.
                    if (toolData.tool) {
                        onVoiceToolEvent('tool', toolData.tool);
                    }
                    // Add a pending placeholder row so the chevron + step count
                    // appear with the chime. tool_complete will upgrade it in place.
                    if (toolData.call_id) {
                        const placeholder = {
                            step: stepLog.length + 1,
                            tool: toolData.tool,
                            tool_label: toolData.tool_label || toolData.tool,
                            args_summary: toolData.args_summary || '',
                            call_id: toolData.call_id,
                            pending: true,
                        };
                        stepLog.push(placeholder);
                        addLoadingStep(placeholder);
                    }
                } else if (toolData.status === 'executing') {
                    if (!document.getElementById('loading-indicator')) showLoading();
                    showStopButton(true);
                    exitBusyQueueIfOpen();
                    updateLoadingText(toolData.message || `${stepPrefix}Running ${toolData.tool}...`);
                } else if (toolData.status === 'subagent_step') {
                    // A delegated specialist's own tool call: a rung of its own,
                    // with the chime and the count, not a caption under Delegate
                    // that each new call overwrites.
                    if (toolData.step_entry) {
                        if (toolData.tool) onVoiceToolEvent('tool', toolData.tool);
                        stepLog.push(toolData.step_entry);
                        addLoadingStep(toolData.step_entry);
                    }
                } else if (toolData.status === 'tool_substep') {
                    // Sub-step progress from a long-running tool (e.g. web_search
                    // deep mode emitting "Searching 2/5…" → "Synthesizing…").
                    // Updates the args text on the matching placeholder row.
                    updateStepSubstep(toolData.call_id, toolData.message);
                } else if (toolData.status === 'tool_complete') {
                    if (toolData.tool) {
                        if (toolData.success) {
                            toolsUsed[toolData.tool] = (toolsUsed[toolData.tool] || 0) + 1;
                        } else {
                            const failKey = toolData.tool + ':failed';
                            toolsUsed[failKey] = (toolsUsed[failKey] || 0) + 1;
                        }
                    }
                    if (toolData.step_entry) {
                        // Replace any matching pending placeholder in stepLog with
                        // the final entry (preserving dispatch order). Fall back to
                        // append if no placeholder is found.
                        const callId = toolData.step_entry.call_id;
                        const placeholderIdx = callId
                            ? stepLog.findIndex(s => s.pending && s.call_id === callId)
                            : -1;
                        if (placeholderIdx >= 0) {
                            // Preserve the placeholder's step number so the displayed
                            // order matches dispatch order, not completion order.
                            toolData.step_entry.step = stepLog[placeholderIdx].step;
                            stepLog[placeholderIdx] = toolData.step_entry;
                            updateLoadingStep(toolData.step_entry);
                        } else {
                            stepLog.push(toolData.step_entry);
                            addLoadingStep(toolData.step_entry);
                        }
                        if (toolData.step_entry.raw_output) {
                            pendingDataBlocks.push({
                                tool_label: toolData.step_entry.tool_label || toolData.step_entry.tool,
                                args_summary: toolData.step_entry.args_summary || '',
                                raw_output: toolData.step_entry.raw_output
                            });
                        }
                    }
                } else if (toolData.status === 'cancelled') {
                    showStopButton(false);
                    updateLoadingText('Stopping...');
                } else if (toolData.status === 'processing') {
                    updateLoadingText(toolData.message || `${stepPrefix}Processing results...`);
                } else if (toolData.status === 'generating') {
                    updateLoadingText(toolData.message || `${stepPrefix}Generating response...`);
                } else if (toolData.status === 'stuck') {
                    showStopButton(false);
                    updateLoadingText(toolData.message || `Stuck detected, finishing up...`);
                } else if (toolData.status === 'max_iterations') {
                    showStopButton(false);
                    updateLoadingText(toolData.message || `Step limit reached, generating response...`);
                } else if (toolData.status === 'token_budget') {
                    showStopButton(false);
                    updateLoadingText(toolData.message || `Token budget reached, generating response...`);
                } else if (toolData.status === 'complete') {
                    // Legacy status from pattern-matched tools
                    if (toolData.success) {
                        if (toolData.tool) {
                            toolsUsed[toolData.tool] = (toolsUsed[toolData.tool] || 0) + 1;
                        }
                        updateLoadingText('Generating response...');
                    } else {
                        updateLoadingText('Generating response...');
                    }
                } else if (toolData.status === 'error') {
                    updateLoadingText('Generating response...');
                }
            },
            // onToolApproval - HITL tool approval request
            (approvalData) => {
                handleToolApproval(approvalData, agent);
            },
            // onVisionStatus - vision pre-processing progress
            (visionData) => {
                if (visionData.status === 'processing') {
                    const count = visionData.count || 1;
                    updateLoadingText(`Analyzing ${count} image${count > 1 ? 's' : ''} with vision model...`);
                } else if (visionData.status === 'complete') {
                    const count = visionData.count || 1;
                    const ms = visionData.duration_ms;
                    const timeStr = ms ? ` (${(ms / 1000).toFixed(1)}s)` : '';
                    updateLoadingText(`Described ${count} image${count > 1 ? 's' : ''}${timeStr}. Generating...`);
                    // Track vision as a tool used
                    toolsUsed['vision'] = (toolsUsed['vision'] || 0) + (visionData.count || 1);
                } else if (visionData.status === 'error') {
                    updateLoadingText('Vision failed. Generating...');
                }
            },
            // onDocReady - document created from response
            // Store link data here; actual DOM insertion happens in onComplete
            // (after renderMessageMarkdown, which overwrites contentDiv)
            (docData) => {
                if (docData.filename && docData.url) {
                    toolsUsed['doc_create'] = (toolsUsed['doc_create'] || 0) + 1;
                    pendingDocLink = docData;
                }
            },
            // onDocStatus - doc create intent detection progress
            (statusData) => {
                if (statusData.status === 'detecting') {
                    updateLoadingText('Detecting document type...');
                } else if (statusData.status === 'detected') {
                    updateLoadingText(`Creating ${statusData.format?.toUpperCase() || 'document'}... Generating...`);
                } else if (statusData.status === 'error') {
                    updateLoadingText('Generating...');
                }
            },
            // onThinkingChunk - inter-step reasoning streamed during tool loops
            (chunk) => {
                if (!assistantDiv) {
                    assistantDiv = addMessage('assistant', '', { header: assistantName, extracting: true, running: true });
                }
                thinkingText += chunk;
                addThinkingToMessage(assistantDiv, thinkingText, false);
                thinkingShown = true;
            }
        );
    } catch (e) {
        flushPendingRender();
        hideLoading();
        const cooldownTriggered = recordFailure();
        if (cooldownTriggered) {
            startCooldownUI(COOLDOWN_MS);
        }
        const errorDiv = addMessage('assistant', `Error: ${e.message}`, { header: assistantName });
        errorDiv.style.color = '#f87171';
        addRetryButton(errorDiv, cooldownTriggered);
    }

    hideLoading();
    state.isStreaming = false;
    setStreamingState(false);
}

/**
 * Add retry, change model, and delete buttons to an error message div
 * @param {HTMLElement} messageDiv - The message div to add buttons to
 * @param {boolean} cooldownTriggered - Whether cooldown was just triggered
 * @param {number} messageId - Optional message ID for delete functionality
 */
function exitBusyQueueIfOpen() {
    if (!document.getElementById('busy-overlay')?.hidden) exitBusyQueue();
}

// ─── "All workers busy" queue overlay ───────────────────────────────────────
// Shown when admission control refuses a request (no worker free, local
// fallback off). Locks the UI, auto-retries the last message on a backoff,
// and offers a "Start over" escape hatch (full reload).
let busyRetryTimer = null;
let busyRetryDelay = 3000;

export function enterBusyQueue() {
    const overlay = document.getElementById('busy-overlay');
    if (!overlay) return;
    overlay.hidden = false;
    const restart = document.getElementById('busy-restart');
    if (restart && !restart._wired) {
        restart._wired = true;
        restart.addEventListener('click', () => window.location.reload());
    }
    scheduleBusyRetry();
}

function exitBusyQueue() {
    const overlay = document.getElementById('busy-overlay');
    if (overlay) overlay.hidden = true;
    if (busyRetryTimer) { clearTimeout(busyRetryTimer); busyRetryTimer = null; }
    busyRetryDelay = 3000;
}

function scheduleBusyRetry() {
    if (busyRetryTimer) clearTimeout(busyRetryTimer);
    busyRetryTimer = setTimeout(async () => {
        if (document.getElementById('busy-overlay')?.hidden) return;
        // Re-send the last message. Content streaming tears the overlay down
        // (exitBusyQueueIfOpen); another busy re-enters via onError with a
        // longer backoff. Either way, schedule the next probe as a safety net.
        busyRetryDelay = Math.min(busyRetryDelay * 1.4, 15000);
        try {
            // lastSentMessage is { message, attachments }: passing the object
            // as the text sent {"message": {...}} and every busy retry came
            // back 422 instead of re-sending.
            const last = state.lastSentMessage || {};
            await retrySendMessage(last.message || '', last.attachments?.length ? last.attachments : getAttachments());
        } catch { /* onError handles busy/real errors */ }
        if (!document.getElementById('busy-overlay')?.hidden) scheduleBusyRetry();
    }, busyRetryDelay);
}

export function addRetryButton(messageDiv, cooldownTriggered, messageId = null) {
    if (!messageDiv || !state.lastSentMessage) return;

    const retryContainer = document.createElement('div');
    retryContainer.className = 'retry-container';

    // Provider-specific hint (rendered above the button row). Populated
    // below when the error type is one we can guide the user through.
    let providerHint = '';

    // Detect if this is an error where "Use Balance" would help (free OpenRouter model)
    const errorText = messageDiv.textContent || '';
    const isFreeTierError = state.loadedModel?.endsWith(':free') &&
        (errorText.includes('rate limit') || errorText.includes('Rate limit') || errorText.includes('429') ||
         errorText.includes('balance') || errorText.includes('free tier') || errorText.includes('capacity limits'));

    // Detect if this is a Google Gemini free-tier limit hit (RESOURCE_EXHAUSTED).
    // Google's tier is set at the project level, not per-request, so we can't
    // strip a URL suffix like we do for OpenRouter. Instead surface a link that
    // opens Google's project dashboard where billing can be enabled.
    const isGoogleQuotaError = state.loadedModel?.startsWith('gemini-') &&
        (errorText.includes('RESOURCE_EXHAUSTED') ||
         errorText.includes('quota') ||
         errorText.includes('429'));

    // Check current cooldown (may already be active from previous failures)
    const currentCooldown = getCooldownRemaining();

    // Build button HTML — mutually exclusive: at most one provider-specific
    // action button appears (Use Balance for OpenRouter, Manage Billing for Google).
    let providerActionBtn = '';
    if (isFreeTierError) {
        providerActionBtn = '<button class="use-balance-btn">Use Balance</button>';
    } else if (isGoogleQuotaError) {
        providerActionBtn = '<a class="manage-billing-btn" href="https://ai.studio/projects" target="_blank" rel="noopener noreferrer">Manage Billing</a>';
        providerHint = `
            <div class="provider-hint">
                Free-tier limit reached (1,500 req/day, 15 RPM per Google project).
                Enable billing on this project to unlock higher limits, wait for the daily reset, or switch models.
            </div>`;
    }
    const useBalanceBtn = providerActionBtn;  // keep var name so downstream template literals still resolve

    if (cooldownTriggered || currentCooldown > 0) {
        // Show retry button with countdown
        const secs = Math.ceil((currentCooldown || COOLDOWN_MS) / 1000);
        retryContainer.innerHTML = `
            ${providerHint}
            ${useBalanceBtn}
            <button class="retry-btn">Retry (<span class="cooldown-timer">${secs}</span>s)</button>
            <button class="change-model-btn">Change Model</button>
            <button class="delete-error-btn">Delete</button>
        `;
        const timerSpan = retryContainer.querySelector('.cooldown-timer');
        const retryBtn = retryContainer.querySelector('.retry-btn');

        // Start countdown display
        const countdownInterval = setInterval(() => {
            const remaining = getCooldownRemaining();
            if (remaining <= 0) {
                clearInterval(countdownInterval);
                retryBtn.textContent = 'Retry';
            } else {
                timerSpan.textContent = Math.ceil(remaining / 1000);
            }
        }, 1000);
    } else {
        // Show buttons immediately
        retryContainer.innerHTML = `
            ${providerHint}
            ${useBalanceBtn}
            <button class="retry-btn">Retry</button>
            <button class="change-model-btn">Change Model</button>
            <button class="delete-error-btn">Delete</button>
        `;
    }

    // Add click handlers
    retryContainer.querySelector('.retry-btn').addEventListener('click', handleRetry);
    retryContainer.querySelector('.change-model-btn').addEventListener('click', handleChangeModelAndRetry);

    // "Use Balance" button - bypass free tier and retry immediately
    const useBalanceBtnEl = retryContainer.querySelector('.use-balance-btn');
    if (useBalanceBtnEl) {
        useBalanceBtnEl.addEventListener('click', handleUseBalanceAndRetry);
    }

    // Delete button - use message ID if available, otherwise use stored ID from element
    const deleteBtn = retryContainer.querySelector('.delete-error-btn');
    const effectiveMessageId = messageId || messageDiv.dataset.messageId;
    if (effectiveMessageId) {
        deleteBtn.addEventListener('click', () => {
            // Import and call the delete modal
            import('./ui.js').then(({ showDeleteConfirmModal }) => {
                showDeleteConfirmModal(effectiveMessageId, messageDiv);
            });
        });
    } else {
        // No message ID - hide delete button
        deleteBtn.style.display = 'none';
    }

    messageDiv.appendChild(retryContainer);
}

/**
 * Handle retry button click - removes error message and resends (keeps user message)
 */
async function handleRetry() {
    const { lastSentMessage } = state;
    if (!lastSentMessage) return;

    // Check cooldown first before removing anything
    const cooldownMs = getCooldownRemaining();
    if (cooldownMs > 0) {
        const secs = Math.ceil(cooldownMs / 1000);
        addSystemMessage(`Cooldown active. Please wait ${secs} seconds.`);
        return;
    }

    // Find the error message and capture its ID for deletion on success
    const retryBtn = document.querySelector('.retry-btn');
    const errorDiv = retryBtn?.closest('.message.assistant');
    const errorMessageId = errorDiv?.dataset?.messageId;

    if (errorDiv) {
        errorDiv.remove();
    }

    // Keep user message - it's already in DOM and DB
    // Just resend without adding another user message
    const messageToRetry = lastSentMessage.message;
    const attachmentsToRetry = lastSentMessage.attachments || [];

    // Use internal retry function that skips user message creation
    // Pass error message ID so it can be deleted on success
    await retrySendMessage(messageToRetry, attachmentsToRetry, errorMessageId);
}

/**
 * Handle "Change Model" button - opens model selector, then retries with new model
 */
async function handleChangeModelAndRetry() {
    const { lastSentMessage } = state;
    if (!lastSentMessage) return;

    // Import and open model manager with a callback
    const { openModelManagerForRetry } = await import('./model-manager.js');

    // Find the error message and capture its ID for deletion on success
    const retryBtn = document.querySelector('.retry-btn');
    const errorDiv = retryBtn?.closest('.message.assistant');
    const errorMessageId = errorDiv?.dataset?.messageId;

    openModelManagerForRetry(async () => {
        // Called after model is loaded - retry the message
        if (errorDiv) {
            errorDiv.remove();
        }
        const messageToRetry = lastSentMessage.message;
        const attachmentsToRetry = lastSentMessage.attachments || [];
        // Pass error message ID so it can be deleted on success
        await retrySendMessage(messageToRetry, attachmentsToRetry, errorMessageId);
    });
}

/**
 * Handle "Use Balance" button - bypass free tier rate limit and retry immediately
 */
async function handleUseBalanceAndRetry() {
    const { lastSentMessage } = state;
    if (!lastSentMessage) return;

    const modelId = state.loadedModel;
    if (!modelId || !modelId.endsWith(':free')) return;

    // Enable bypass for this model (strips :free on next request)
    setBypassed(modelId, true);

    // Clear cooldown — we're switching to paid, free tier failures don't apply
    clearCooldown();
    clearCooldownUI();

    // Find the error message and capture its ID for deletion on success
    const useBalanceBtn = document.querySelector('.use-balance-btn');
    const errorDiv = useBalanceBtn?.closest('.message.assistant');
    const errorMessageId = errorDiv?.dataset?.messageId;

    if (errorDiv) {
        errorDiv.remove();
    }

    // Retry the message — isBypassed() will cause sendMessage to strip :free
    const messageToRetry = lastSentMessage.message;
    const attachmentsToRetry = lastSentMessage.attachments || [];
    await retrySendMessage(messageToRetry, attachmentsToRetry, errorMessageId);
}

/**
 * Internal retry send - skips user message creation since it already exists
 * @param {string} message - The message to retry
 * @param {Array} attachments - Attachments to include
 * @param {string} errorMessageId - Optional ID of error message to delete on success
 */
async function retrySendMessage(message, attachments = [], errorMessageId = null) {
    if (state.isStreaming) return;

    // Validate that a model is loaded
    if (!state.loadedModel) {
        addSystemMessage('No model loaded. Open settings and load a model first.');
        return;
    }

    const agent = state.loadedAgent || elements.agentSelect.value;
    const collection = state.loadedCollection || null;
    let model = state.loadedModel;

    // Stream-handler state (mirrors the primary send handler). These were missing
    // here, so the retry path's search-status callback threw a ReferenceError when
    // it recorded image results / thinking state.
    let thinkingShown = false;
    let pendingImageResults = null;

    // If this is a bypassed free model, strip :free to use balance
    if (model && model.endsWith(':free') && isBypassed(model)) {
        model = model.replace(':free', '');
    }

    // Prepare attachments for API
    const attachmentsForApi = attachments.length > 0
        ? attachments.map(att => ({
            id: att.id,
            type: att.type,
            name: att.name,
            content: att.content,
            size: att.size,
            media_type: att.media_type || undefined
        }))
        : null;

    lastDispatchedTurn = { message, attachments: attachmentsForApi };

    // Use collection title for display
    const assistantName = getAssistantName(agent || state.loadedCollectionTitle || collection);

    state.isStreaming = true;
    setStreamingState(true);
    showLoading();

    let assistantDiv = null;
    let fullText = '';

    try {
        await api.sendChatMessage(
            {
                message: message,
                sessionId: state.sessionId,
                model,
                agent: agent || undefined,
                collection: collection || undefined,
                temperature: state.temperature,
                fidelity: state.fidelity,
                memoryLevel: state.memoryLevel,
                kbThreshold: state.agentSettings?.agent_kb_threshold ?? undefined,
                debugMode: state.debugMode,
                webSearchEnabled: state.webSearchEnabled,
                webSearchDepth: state.webSearchDepth,
                webSearchImages: state.imageSearchEnabled && state.webSearchEnabled,
                narratorMode: state.narratorMode,
                narratorState: state.narratorState,
                disabledTools: [...state.disabledTools],
                attachments: attachmentsForApi
            },
            // onChunk
            (chunk) => {
                if (!assistantDiv) {
                    hideLoading();
                    assistantDiv = addMessage('assistant', '', { header: assistantName, extracting: true });
                }
                fullText += chunk;
                if (isVoiceActive()) onVoiceStreamChunk(chunk);
                throttledRender(assistantDiv, fullText);
            },
            // onComplete
            (data) => {
                flushPendingRender();

                if (!assistantDiv) {
                    hideLoading();
                    if (data.cancelled) {
                        assistantDiv = addMessage('assistant', '', { header: assistantName, cancelled: true, hasSession: !!(data.session_id || state.sessionId) });
                    } else {
                        assistantDiv = addMessage('assistant', '', { header: assistantName, extracting: true });
                    }
                }

                const renderText = data.cancelled
                    ? _decorateCancelledContent(data.full_text)
                    : data.full_text;
                if (data.cancelled) {
                    markMessageCancelled(assistantDiv, !!(data.session_id || state.sessionId), data.message_id);
                }
                renderMessageMarkdown(assistantDiv, renderText);

                if (data.session_id) {
                    state.sessionId = data.session_id;
                    saveSessionStateWithModel(model);
                }

                // Add meta info
                let metaText = '';
                if (data.kb_top && (agent || collection) && state.agentKbCount > 3) metaText += `KB Top: ${data.kb_top}`;
                if (data.model) metaText += (metaText ? ' | ' : '') + `Model: ${data.model}`;
                if (metaText) {
                    addMetaToMessage(assistantDiv, metaText);
                }

                // Track usage
                trackModelUsage(model || data.model, message, data.full_text, data.session_id);

                // Poll for extraction (skip for cancelled - no extraction ran)
                if (!data.cancelled && data.session_id && assistantDiv) {
                    pollExtractionStatus(data.session_id, assistantDiv);
                }

                // Success - reset failure counter and clear retry state
                recordSuccess();
                clearLastSentMessage();
                clearCooldownUI();

                // Delete the old error message from DB if this was a retry
                if (errorMessageId && state.sessionId) {
                    fetch(`/sessions/${state.sessionId}/messages/${errorMessageId}`, {
                        method: 'DELETE',
                        headers: api.getHeaders(false)
                    }).catch(() => {});
                }

                // Build and save per-message analytics (async, non-blocking)
                buildAndSaveMetadata(assistantDiv, data, message, data.session_id || state.sessionId);

                scrollToBottom();
            },
            // onError
            async (errorMsg, partialText, errorSessionId) => {
                flushPendingRender();
                hideLoading();

                if (errorSessionId && !state.sessionId) {
                    state.sessionId = errorSessionId;
                }

                const cooldownTriggered = recordFailure();
                if (cooldownTriggered) {
                    startCooldownUI(COOLDOWN_MS);
                }

                // Construct the error message content for saving
                let errorContent;
                if (partialText) {
                    errorContent = partialText + '\n\n---\n*⚠️ Response interrupted: ' + errorMsg + '*';
                } else {
                    errorContent = `⚠️ Error: ${errorMsg}`;
                }

                // Save error message to database for retry persistence and get message ID
                let savedMessageId = null;
                if (state.sessionId) {
                    try {
                        const result = await api.saveErrorMessage(state.sessionId, errorContent, state.loadedModel, lastDispatchedTurn);
                        savedMessageId = result.message_id || null;
                    } catch {
                    }
                }

                if (partialText && assistantDiv) {
                    if (savedMessageId) assistantDiv.dataset.messageId = savedMessageId;
                    renderMessageMarkdown(assistantDiv, errorContent);
                    addRetryButton(assistantDiv, cooldownTriggered, savedMessageId);
                } else if (partialText) {
                    assistantDiv = addMessage('assistant', '', { header: assistantName });
                    if (savedMessageId) assistantDiv.dataset.messageId = savedMessageId;
                    renderMessageMarkdown(assistantDiv, errorContent);
                    addRetryButton(assistantDiv, cooldownTriggered, savedMessageId);
                } else {
                    const errorDiv = addMessage('assistant', errorContent, { header: assistantName });
                    if (savedMessageId) errorDiv.dataset.messageId = savedMessageId;
                    errorDiv.style.color = '#f87171';
                    addRetryButton(errorDiv, cooldownTriggered, savedMessageId);
                }

                state.isStreaming = false;
                setStreamingState(false);
                scrollToBottom();
            },
            // onSearchStatus - web/doc search progress updates
            (searchData) => {
                const depthLabels = {
                    'surface': 'Searching',
                    'shallow': 'Deep searching',
                    'deep': 'Researching'
                };
                if (searchData.status === 'deciding') {
                    updateLoadingText('Analyzing request...');
                } else if (searchData.status === 'searching') {
                    showStopButton(true);
                    exitBusyQueueIfOpen();
                    _wireStopButton();
                    updateLoadingText(`${depthLabels[searchData.depth] || 'Searching'}...`);
                } else if (searchData.status === 'cancelled') {
                    showStopButton(false);
                    updateLoadingText('Stopping...');
                } else if (searchData.status === 'searching_docs') {
                    const query = searchData.query;
                    showStopButton(true);
                    exitBusyQueueIfOpen();
                    _wireStopButton();
                    updateLoadingText(query ? `Searching docs: "${query}"...` : 'Searching knowledge base...');
                } else if (searchData.status === 'docs_complete') {
                    const count = searchData.result_count || 0;
                    const searchQuery = searchData.search_query;
                    if (count === 0) {
                        updateLoadingText(searchQuery
                            ? `"${searchQuery}" → No docs found. Generating...`
                            : 'No docs found. Generating...');
                    } else {
                        updateLoadingText(searchQuery
                            ? `"${searchQuery}" → ${count} docs. Generating...`
                            : `Found ${count} docs. Generating...`);
                    }
                } else if (searchData.status === 'complete') {
                    const searchQuery = searchData.search_query;
                    const count = searchData.result_count || 0;
                    const fetched = searchData.fetched_count || 0;
                    const hasSummary = searchData.has_summary;

                    let statusText;
                    if (count === 0) {
                        statusText = searchQuery
                            ? `"${searchQuery}" → No sources found. Generating...`
                            : 'No sources found. Generating...';
                    } else if (searchData.depth === 'deep' && hasSummary) {
                        statusText = `Analyzed ${fetched} sources. Generating...`;
                    } else if (searchData.depth === 'shallow' && fetched > 0) {
                        statusText = `Read ${fetched} pages. Generating...`;
                    } else {
                        statusText = searchQuery
                            ? `"${searchQuery}" → ${count} results. Generating...`
                            : `Found ${count} results. Generating...`;
                    }
                    updateLoadingText(statusText);
                    // Capture image results for rendering after response completes
                    if (searchData.image_results && searchData.image_results.length > 0) {
                        pendingImageResults = searchData.image_results;
                    }
                } else if (searchData.status === 'error') {
                    updateLoadingText('Search failed. Generating...');
                }
            },
            // onToolStatus
            (toolData) => {
                if (isVoiceActive() && (toolData.status === 'planning' || toolData.status === 'found_tool')) {
                    onVoiceToolInterrupt();
                }
                if (toolData.status === 'planning') {
                    updateLoadingText(toolData.message || 'Planning approach...');
                    if (toolData.plan_text) {
                        if (!assistantDiv) {
                            // See comment in primary handler — leave the loader
                            // in place so it stays above the assistant message.
                            assistantDiv = addMessage('assistant', '', { header: assistantName, extracting: true, running: true });
                        }
                        addThinkingToMessage(assistantDiv, toolData.plan_text, true);
                        thinkingShown = true;
                    }
                } else if (toolData.status === 'tool_error') {
                    // A hard provider limit used to reach the user as "it just
                    // didn't call any tools". Say what happened, and make it
                    // stick around longer than a spinner caption would.
                    updateLoadingText(toolData.message || 'Tools unavailable — answering without them...');
                    showToast(toolData.message || 'Tools unavailable for this turn', 'error', 9000);
                } else if (toolData.status === 'thinking') {
                    updateLoadingText(toolData.message || 'Deciding tool...');
                } else if (toolData.status === 'found_tool') {
                    updateLoadingText(toolData.message || `Using ${toolData.tool}...`);
                } else if (toolData.status === 'executing') {
                    updateLoadingText(toolData.message || `Running ${toolData.tool}...`);
                } else if (toolData.status === 'tool_substep') {
                    // Abbreviated handler has no row tracking — surface the
                    // sub-step in the spinner text instead.
                    if (toolData.message) updateLoadingText(toolData.message);
                } else if (toolData.status === 'generating') {
                    updateLoadingText(toolData.message || 'Generating response...');
                }
            }
        );
    } catch (e) {
        flushPendingRender();
        hideLoading();
        const cooldownTriggered = recordFailure();
        if (cooldownTriggered) {
            startCooldownUI(COOLDOWN_MS);
        }
        const errorContent = `Error: ${e.message}`;
        // Save error message to database for retry persistence and get message ID
        let savedMessageId = null;
        if (state.sessionId) {
            try {
                const result = await api.saveErrorMessage(state.sessionId, errorContent, state.loadedModel, lastDispatchedTurn);
                savedMessageId = result.message_id || null;
            } catch {
            }
        }
        const errorDiv = addMessage('assistant', errorContent, { header: assistantName });
        if (savedMessageId) errorDiv.dataset.messageId = savedMessageId;
        errorDiv.style.color = '#f87171';
        addRetryButton(errorDiv, cooldownTriggered, savedMessageId);
    }

    hideLoading();
    state.isStreaming = false;
    setStreamingState(false);
}

/**
 * Send a message with specified attachments (used by redo feature).
 * This bypasses the normal input handling and directly sends with provided attachments.
 * No web search support - regenerations use only local context.
 *
 * @param {string} message - The message text
 * @param {Array} attachments - Array of attachment objects {id, type, name, content, size}
 * @param {Object} [opts]
 * @param {boolean} [opts.skipUserBubble=false] - Don't add the default user
 *   message bubble. Used by the Ingest Document flow which renders its own
 *   document-style user bubble before calling this.
 */
export async function sendMessageWithAttachments(message, attachments = [], opts = {}) {
    // Allow an empty message when media is attached. A promptless VIDEO is
    // always sendable — its transcript speaks even with no vision anywhere,
    // and the server owns the can-anything-see decision. A promptless IMAGE
    // still needs a local vision signal.
    //
    // This gate MUST NOT silently drop a redo: the redo flow deletes the old
    // pair before calling here, so a quiet `return` at this line turned a
    // resubmit into pure deletion — the turn vanished with no error and no
    // replacement. That happened live.
    const hasVideo = attachments.some(a => a.type === 'video');
    const hasImageVisionSubmit =
        attachments.some(a => a.type === 'image' || a.type === 'video') &&
        (!!(state.agentSettings && state.agentSettings.tool_vision) || loadedModelSupportsVision());
    if (!message?.trim() && !hasVideo && !hasImageVisionSubmit) {
        addSystemMessage('Nothing was sent: add a prompt, or load a vision model to re-describe an image.');
        return;
    }
    if (state.isStreaming) return;
    resetToolCallPitch();

    // Validate that a model is loaded
    if (!state.loadedModel) {
        addSystemMessage('No model loaded. Open settings and load a model first.');
        return;
    }

    // Wait for any pending fact extraction to complete
    await waitForExtraction();

    const agent = state.loadedAgent || elements.agentSelect.value;
    const collection = state.loadedCollection || null;
    const model = state.loadedModel || undefined;
    const userQuestion = message;

    const hasAttachments = attachments.length > 0;
    const attachmentsForApi = hasAttachments
        ? attachments.map(att => ({
            id: att.id,
            type: att.type,
            name: att.name,
            // Same shape as sendMessage: a stored picture is its media_id.
            // Dropping the id here sent a redone photo as an image with no
            // bytes and nothing to fetch them by, and a sighted chat model
            // failed the whole turn on it ("Failed to load image").
            content: att.media_id && att.type === 'image' ? '' : att.content,
            media_id: att.media_id || undefined,
            meta: att.meta || undefined,
            size: att.size,
            media_type: att.media_type || undefined
        }))
        : null;

    // Determine assistant name for header (include collection context)
    const assistantName = getAssistantName(agent || state.loadedCollectionTitle || collection);

    lastDispatchedTurn = { message: userQuestion, attachments: attachmentsForApi };

    // Show user message with attachment chips if applicable. The Ingest
    // Document flow already added its own document-style user bubble
    // before calling here, so it passes skipUserBubble: true.
    if (!opts.skipUserBubble) {
        addMessage('user', message, {
            header: 'You',
            render: true,
            attachments: hasAttachments ? attachments : undefined
        });
    }

    state.isStreaming = true;
    setStreamingState(true);

    // Show loading spinner. (Stop wiring is no longer per-request — the send
    // button is the stop button now, wired once at app init.)
    showLoading();

    let assistantDiv = null;
    let fullText = '';
    let thinkingShown = false;
    let voicedResponseLen = 0;
    let toolsUsed = {};
    let stepLog = [];
    let pendingDataBlocks = [];
    let inToolLoop = false;
    let pendingDocLink = null;
    let pendingImageResults = null;
    let thinkingText = '';

    try {
        await api.sendChatMessage(
            {
                message: message,
                sessionId: state.sessionId,
                model,
                agent: agent || undefined,
                collection: collection || undefined,
                temperature: state.temperature,
                fidelity: state.fidelity,
                memoryLevel: state.memoryLevel,
                kbThreshold: state.agentSettings?.agent_kb_threshold ?? undefined,
                debugMode: state.debugMode,
                webSearchEnabled: state.webSearchEnabled,
                webSearchDepth: state.webSearchDepth,
                narratorMode: state.narratorMode,
                narratorState: state.narratorState,
                disabledTools: [...state.disabledTools],
                attachments: attachmentsForApi
            },
            // onChunk - throttled markdown rendering for smooth output
            // null = retract signal (see primary handler)
            (chunk) => {
                if (chunk === null) {
                    fullText = '';
                    voicedResponseLen = 0;
                    if (assistantDiv) {
                        const contentDiv = assistantDiv.querySelector('.message-content');
                        if (contentDiv) contentDiv.innerHTML = '';
                    }
                    if (isVoiceActive()) onVoiceRetract();
                    return;
                }
                // Finalize thinking dropdown when response text starts streaming
                if (thinkingText && thinkingShown) {
                    addThinkingToMessage(assistantDiv, thinkingText, true);
                }
                if (!assistantDiv) {
                    if (!inToolLoop) {
                        hideLoading();
                    }
                    // In tool loop: spinner stays in place, message appears below it
                    assistantDiv = addMessage('assistant', '', { header: assistantName, extracting: true, running: inToolLoop });
                    if (state.narratorMode) highlightNarratorMessage(assistantDiv);
                }
                fullText += chunk;

                // Handle reasoning model thinking content
                if (hasThinkingContent(fullText)) {
                    const { thinking, response, isComplete } = separateThinkingContent(fullText);

                    if (thinking) {
                        addThinkingToMessage(assistantDiv, thinking, isComplete);
                        thinkingShown = true;
                    }

                    if (response) {
                        throttledRender(assistantDiv, response);
                        // Feed response delta (not thinking) to voice TTS.
                        // Speculative inter-step text may still be voiced here,
                        // but onVoiceRetract() halts and flushes in-flight audio
                        // the instant the server retracts it, so a leak is cut
                        // short rather than read as a full paragraph.
                        if (isVoiceActive() && isComplete) {
                            const delta = response.slice(voicedResponseLen);
                            if (delta) onVoiceStreamChunk(delta);
                            voicedResponseLen = response.length;
                        }
                    }
                } else {
                    throttledRender(assistantDiv, fullText);
                    if (isVoiceActive()) onVoiceStreamChunk(chunk);
                }
            },
            // onComplete
            (data) => {
                flushPendingRender();

                if (!assistantDiv) {
                    hideLoading();
                    if (data.cancelled) {
                        assistantDiv = addMessage('assistant', '', { header: assistantName, cancelled: true, hasSession: !!(data.session_id || state.sessionId) });
                    } else {
                        assistantDiv = addMessage('assistant', '', { header: assistantName, extracting: true });
                    }
                }

                // Handle final render for reasoning models
                let finalContent = data.full_text;
                if (hasThinkingContent(data.full_text)) {
                    const { thinking, response, isComplete } = separateThinkingContent(data.full_text);

                    if (thinking) {
                        // Server-measured duration wins over any client guess
                        addThinkingToMessage(assistantDiv, thinking, true, data.thinking_ms ?? null);
                    }
                    finalContent = response;
                }

                // Strip doc_create download link from markdown before rendering
                if (pendingDocLink) {
                    const linkPattern = /\n\n---\n\[[^\]]+\]\(\/documents\/[^)]+\)$/;
                    finalContent = finalContent.replace(linkPattern, '');
                }

                if (data.cancelled) {
                    finalContent = _decorateCancelledContent(finalContent);
                    markMessageCancelled(assistantDiv, !!(data.session_id || state.sessionId), data.message_id);
                }

                renderMessageMarkdown(assistantDiv, finalContent);

                // Insert doc_create download link in message footer
                if (pendingDocLink && assistantDiv) {
                    const actionBar = assistantDiv.querySelector('.message-actions');
                    const ext = pendingDocLink.filename.split('.').pop()?.toUpperCase() || 'FILE';
                    const linkEl = document.createElement('div');
                    linkEl.className = 'doc-download-bar';
                    const btn = document.createElement('button');
                    btn.className = 'doc-download-link';
                    btn.innerHTML = `<span class="doc-download-icon">&#8681;</span> ${escapeHtml(pendingDocLink.filename)}<span class="doc-download-badge">${escapeHtml(ext)}</span>`;
                    btn.addEventListener('click', async () => {
                        try {
                            // getHeaders carries the container and capsule
                            // tokens too; with only X-Auth-Token this download
                            // came back 401 for anyone inside a container.
                            const { getHeaders } = await import('./api.js');
                            const res = await fetch(pendingDocLink.url, { headers: getHeaders(false) });
                            if (!res.ok) throw new Error(`Download failed: ${res.status}`);
                            const blob = await res.blob();
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = pendingDocLink.filename;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            URL.revokeObjectURL(url);
                        } catch (e) {
                            btn.classList.add('expired');
                            btn.innerHTML = `<span class="doc-download-icon">&#8681;</span> ${escapeHtml(pendingDocLink.filename)}<span class="doc-download-badge expired-badge">EXPIRED</span>`;
                            btn.disabled = true;
                        }
                    });
                    linkEl.appendChild(btn);
                    if (actionBar) {
                        actionBar.parentNode.insertBefore(linkEl, actionBar);
                    } else {
                        assistantDiv.appendChild(linkEl);
                    }
                }

                if (data.session_id) {
                    state.sessionId = data.session_id;
                    saveSessionStateWithModel(model);
                }

                // Add meta info
                let metaText = '';
                if (data.kb_top && (agent || collection) && state.agentKbCount > 3) metaText += `KB Top: ${data.kb_top}`;
                if (data.model && !data.verbatim) metaText += (metaText ? ' | ' : '') + `Model: ${data.model}`;
                if (data.narrator) {
                    // Set DOM attributes FIRST so badge count picks them up
                    if (assistantDiv) {
                        assistantDiv.dataset.narratorChunkId = data.narrator.chunk_id;
                        assistantDiv.dataset.narratorTotalChunks = data.narrator.total_chunks;
                        assistantDiv.dataset.narratorSection = data.narrator.section;
                        assistantDiv.dataset.narratorBook = data.narrator.book || '';
                        assistantDiv.dataset.narratorKbPath = data.narrator.kb_path || '';
                        assistantDiv.dataset.narratorChunkCount = data.narrator.chunk_count || 1;
                    }
                    metaText += (metaText ? ' | ' : '') + `${data.narrator.section} (${data.narrator.chunk_count > 1 ? data.narrator.chunk_id + '-' + (data.narrator.chunk_id + data.narrator.chunk_count - 1) : data.narrator.chunk_id}/${data.narrator.total_chunks})`;

                    state.narratorState.book = data.narrator.book;
                    state.narratorState.kb_path = data.narrator.kb_path;
                    state.narratorState.chunk_id = data.narrator.next_chunk_id || (data.narrator.chunk_id + 1);
                    updateModeIndicators(state.loadedAgent, state.loadedCollection, state.narratorMode, state.narratorState.chunk_id);
                    saveSessionStateWithModel(model);
                    saveNarratorProgress(state.sessionId, state.narratorState);
                    updateNextButtonLabel();
                }
                if (metaText) {
                    addMetaToMessage(assistantDiv, metaText);
                }

                // Show debug log in console if present
                if (data.debug_log && data.debug_log.length > 0) {
                    console.log('%c[RAGSMITH DEBUG]', 'color: #e94560; font-weight: bold');
                    data.debug_log.forEach(line => console.log(line));
                }

                // Show routing matches for agent/collection
                if ((agent || collection) && (!state.narratorMode || state.narratorPaused) && !state.isLightAgent) {
                    if (data.routing_matches && data.routing_matches.length > 0) {
                        showRoutingMatches(assistantDiv, data.routing_matches);
                    }
                }

                // Show tools used badges in message footer
                if (Object.keys(toolsUsed).length > 0) {
                    showToolsUsed(assistantDiv, toolsUsed);
                }

                // Render web image search results if available
                if (pendingImageResults && pendingImageResults.length > 0 && assistantDiv) {
                    showImageSearchResults(assistantDiv, pendingImageResults);
                    pendingImageResults = null;
                }

                // Solidify loading steps into permanent accordion
                const finalStepLog = data.step_log || (stepLog.length > 0 ? stepLog : null);
                solidifyLoadingSteps(assistantDiv, finalStepLog);

                // Render verbatim data blocks from management tools
                if (pendingDataBlocks.length > 0 && assistantDiv) {
                    renderDataBlocks(assistantDiv, pendingDataBlocks);
                }

                // Hide stop button
                showStopButton(false);

                // Build and save per-message analytics (async, non-blocking).
                // The composer path has always done this; this one — used by
                // Redo and by Ingest Document — never did, so a redone turn
                // rendered with no time, no token count and no cost. That is
                // the one control you reach for when comparing two models on
                // the same prompt, and it was silently blank exactly there.
                buildAndSaveMetadata(assistantDiv, data, message, data.session_id || state.sessionId);

                // Track model usage
                const hasToolTokens = data.token_usage && data.token_usage.total_tokens > 0;
                trackModelUsage(
                    model || data.model,
                    message,
                    data.full_text,
                    data.session_id,
                    hasToolTokens ? 'tool_loop' : 'chat',
                    data.token_usage || null
                );

                // Poll for extraction completion (skip for cancelled - no extraction ran)
                if (!data.cancelled && data.session_id && assistantDiv) {
                    pollExtractionStatus(data.session_id, assistantDiv);
                }

                // Fetch and display citations for agent/collection chats
                if ((agent || collection) && (!state.narratorMode || state.narratorPaused)) {
                    api.fetchCitations({
                        agent,
                        collection,
                        question: message,
                        response: data.full_text,
                        sessionId: data.session_id
                    }).then(citations => {
                        if (citations && citations.length > 0) {
                            updateCitations(assistantDiv, citations);
                        }
                    }).catch(() => {});
                }

                // Success - reset failure counter
                recordSuccess();
                clearLastSentMessage();
                clearCooldownUI();

                // Trigger TTS if voice mode is active
                if (isVoiceActive()) {
                    const isVerbatimNarrator2 = state.narratorMode && !state.narratorPaused && state.agentSettings?.narrator_verbatim;
                    if (isVoiceNarratorActive() && isVerbatimNarrator2) {
                        autoPlayNarratorTts(data.full_text);
                    } else {
                        onVoiceResponseComplete(data.full_text);
                    }
                }

                scrollToBottom();
            },
            // onError
            async (errorMsg, partialText, errorSessionId) => {
                flushPendingRender();
                showStopButton(false);
                hideLoading();

                if (errorSessionId && !state.sessionId) {
                    state.sessionId = errorSessionId;
                }

                const cooldownTriggered = recordFailure();
                if (cooldownTriggered) {
                    startCooldownUI(COOLDOWN_MS);
                }

                let errorContent;
                if (partialText) {
                    errorContent = partialText + '\n\n---\n*⚠️ Response interrupted: ' + errorMsg + '*';
                } else {
                    errorContent = `⚠️ Error: ${errorMsg}`;
                }

                // Save error message to database for retry persistence
                let savedMessageId = null;
                if (state.sessionId) {
                    try {
                        const result = await api.saveErrorMessage(state.sessionId, errorContent, state.loadedModel, lastDispatchedTurn);
                        savedMessageId = result.message_id || null;
                    } catch {
                    }
                }

                if (partialText && assistantDiv) {
                    if (savedMessageId) assistantDiv.dataset.messageId = savedMessageId;
                    renderMessageMarkdown(assistantDiv, errorContent);
                    addRetryButton(assistantDiv, cooldownTriggered, savedMessageId);
                } else if (partialText) {
                    assistantDiv = addMessage('assistant', '', { header: assistantName });
                    if (savedMessageId) assistantDiv.dataset.messageId = savedMessageId;
                    renderMessageMarkdown(assistantDiv, errorContent);
                    addRetryButton(assistantDiv, cooldownTriggered, savedMessageId);
                } else {
                    const errorDiv = addMessage('assistant', errorContent, { header: assistantName });
                    if (savedMessageId) errorDiv.dataset.messageId = savedMessageId;
                    errorDiv.style.color = '#f87171';
                    addRetryButton(errorDiv, cooldownTriggered, savedMessageId);
                }

                state.isStreaming = false;
                setStreamingState(false);

                if (isVoiceActive()) {
                    onVoiceResponseComplete('');
                }

                scrollToBottom();
            },
            // onSearchStatus - web/doc search progress updates
            (searchData) => {
                const depthLabels = {
                    'surface': 'Searching',
                    'shallow': 'Deep searching',
                    'deep': 'Researching'
                };
                if (searchData.status === 'deciding') {
                    updateLoadingText('Analyzing request...');
                } else if (searchData.status === 'searching') {
                    showStopButton(true);
                    exitBusyQueueIfOpen();
                    _wireStopButton();
                    updateLoadingText(`${depthLabels[searchData.depth] || 'Searching'}...`);
                } else if (searchData.status === 'cancelled') {
                    showStopButton(false);
                    updateLoadingText('Stopping...');
                } else if (searchData.status === 'searching_docs') {
                    const query = searchData.query;
                    showStopButton(true);
                    exitBusyQueueIfOpen();
                    _wireStopButton();
                    updateLoadingText(query ? `Searching docs: "${query}"...` : 'Searching knowledge base...');
                } else if (searchData.status === 'docs_complete') {
                    const count = searchData.result_count || 0;
                    const searchQuery = searchData.search_query;
                    if (count === 0) {
                        updateLoadingText(searchQuery
                            ? `"${searchQuery}" → No docs found. Generating...`
                            : 'No docs found. Generating...');
                    } else {
                        updateLoadingText(searchQuery
                            ? `"${searchQuery}" → ${count} docs. Generating...`
                            : `Found ${count} docs. Generating...`);
                    }
                } else if (searchData.status === 'complete') {
                    const searchQuery = searchData.search_query;
                    const count = searchData.result_count || 0;
                    const fetched = searchData.fetched_count || 0;
                    const hasSummary = searchData.has_summary;

                    let statusText;
                    if (count === 0) {
                        statusText = searchQuery
                            ? `"${searchQuery}" → No sources found. Generating...`
                            : 'No sources found. Generating...';
                    } else if (searchData.depth === 'deep' && hasSummary) {
                        statusText = `Analyzed ${fetched} sources. Generating...`;
                    } else if (searchData.depth === 'shallow' && fetched > 0) {
                        statusText = `Read ${fetched} pages. Generating...`;
                    } else {
                        statusText = searchQuery
                            ? `"${searchQuery}" → ${count} results. Generating...`
                            : `Found ${count} results. Generating...`;
                    }
                    updateLoadingText(statusText);
                    if (searchData.image_results && searchData.image_results.length > 0) {
                        pendingImageResults = searchData.image_results;
                    }
                } else if (searchData.status === 'error') {
                    updateLoadingText('Search failed. Generating...');
                }
            },
            // onToolStatus - tool execution progress updates (supports multi-step)
            (toolData) => {
                const step = toolData.step;
                const maxSteps = toolData.max_steps;
                const stepPrefix = step && step > 1 ? `Step ${step}: ` : '';

                // Mark that we're in a tool loop (spinner stays visible alongside text)
                inToolLoop = true;

                // Flush active TTS session before tool execution pauses the text stream
                if (isVoiceActive() && (toolData.status === 'planning' || toolData.status === 'found_tool')) {
                    onVoiceToolInterrupt();
                }

                if (toolData.status === 'planning') {
                    updateLoadingText(toolData.message || 'Planning approach...');
                    if (toolData.plan_text) {
                        if (!assistantDiv) {
                            // See comment in primary handler — leave the loader
                            // in place so it stays above the assistant message.
                            assistantDiv = addMessage('assistant', '', { header: assistantName, extracting: true, running: true });
                        }
                        addThinkingToMessage(assistantDiv, toolData.plan_text, true);
                        thinkingShown = true;
                    }
                } else if (toolData.status === 'tool_error') {
                    // A hard provider limit used to reach the user as "it just
                    // didn't call any tools". Say what happened, and make it
                    // stick around longer than a spinner caption would.
                    updateLoadingText(toolData.message || 'Tools unavailable — answering without them...');
                    showToast(toolData.message || 'Tools unavailable for this turn', 'error', 9000);
                } else if (toolData.status === 'thinking') {
                    if (!document.getElementById('loading-indicator')) showLoading(assistantDiv);
                    updateLoadingText(toolData.message || `${stepPrefix}Deciding tool...`);
                } else if (toolData.status === 'found_tool') {
                    if (!document.getElementById('loading-indicator')) showLoading(assistantDiv);
                    showStopButton(true);
                    exitBusyQueueIfOpen();
                    _wireStopButton();
                    updateLoadingText(toolData.message || `${stepPrefix}Using ${toolData.tool}...`);
                    // Chime on tool START — see comment in primary handler above.
                    if (toolData.tool) {
                        onVoiceToolEvent('tool', toolData.tool);
                    }
                    // Add a pending placeholder row so the chevron + step count
                    // appear with the chime. tool_complete will upgrade it in place.
                    if (toolData.call_id) {
                        const placeholder = {
                            step: stepLog.length + 1,
                            tool: toolData.tool,
                            tool_label: toolData.tool_label || toolData.tool,
                            args_summary: toolData.args_summary || '',
                            call_id: toolData.call_id,
                            pending: true,
                        };
                        stepLog.push(placeholder);
                        addLoadingStep(placeholder);
                    }
                } else if (toolData.status === 'executing') {
                    if (!document.getElementById('loading-indicator')) showLoading(assistantDiv);
                    showStopButton(true);
                    exitBusyQueueIfOpen();
                    updateLoadingText(toolData.message || `${stepPrefix}Running ${toolData.tool}...`);
                } else if (toolData.status === 'subagent_step') {
                    // A delegated specialist's own tool call: a rung of its own,
                    // with the chime and the count, not a caption under Delegate
                    // that each new call overwrites.
                    if (toolData.step_entry) {
                        if (toolData.tool) onVoiceToolEvent('tool', toolData.tool);
                        stepLog.push(toolData.step_entry);
                        addLoadingStep(toolData.step_entry);
                    }
                } else if (toolData.status === 'tool_substep') {
                    // Sub-step progress — see comment in primary handler.
                    updateStepSubstep(toolData.call_id, toolData.message);
                } else if (toolData.status === 'tool_complete') {
                    if (toolData.tool) {
                        if (toolData.success) {
                            toolsUsed[toolData.tool] = (toolsUsed[toolData.tool] || 0) + 1;
                        } else {
                            const failKey = toolData.tool + ':failed';
                            toolsUsed[failKey] = (toolsUsed[failKey] || 0) + 1;
                        }
                    }
                    if (toolData.step_entry) {
                        // Replace any matching pending placeholder (see primary handler).
                        const callId = toolData.step_entry.call_id;
                        const placeholderIdx = callId
                            ? stepLog.findIndex(s => s.pending && s.call_id === callId)
                            : -1;
                        if (placeholderIdx >= 0) {
                            toolData.step_entry.step = stepLog[placeholderIdx].step;
                            stepLog[placeholderIdx] = toolData.step_entry;
                            updateLoadingStep(toolData.step_entry);
                        } else {
                            stepLog.push(toolData.step_entry);
                            addLoadingStep(toolData.step_entry);
                        }
                        if (toolData.step_entry.raw_output) {
                            pendingDataBlocks.push({
                                tool_label: toolData.step_entry.tool_label || toolData.step_entry.tool,
                                args_summary: toolData.step_entry.args_summary || '',
                                raw_output: toolData.step_entry.raw_output
                            });
                        }
                    }
                } else if (toolData.status === 'cancelled') {
                    showStopButton(false);
                    updateLoadingText('Stopping...');
                } else if (toolData.status === 'processing') {
                    updateLoadingText(toolData.message || `${stepPrefix}Processing results...`);
                } else if (toolData.status === 'generating') {
                    updateLoadingText(toolData.message || `${stepPrefix}Generating response...`);
                } else if (toolData.status === 'stuck') {
                    showStopButton(false);
                    updateLoadingText(toolData.message || `Stuck detected, finishing up...`);
                } else if (toolData.status === 'max_iterations') {
                    showStopButton(false);
                    updateLoadingText(toolData.message || `Step limit reached, generating response...`);
                } else if (toolData.status === 'token_budget') {
                    showStopButton(false);
                    updateLoadingText(toolData.message || `Token budget reached, generating response...`);
                } else if (toolData.status === 'complete') {
                    if (toolData.success) {
                        if (toolData.tool) {
                            toolsUsed[toolData.tool] = (toolsUsed[toolData.tool] || 0) + 1;
                        }
                        updateLoadingText('Generating response...');
                    } else {
                        updateLoadingText('Generating response...');
                    }
                } else if (toolData.status === 'error') {
                    updateLoadingText('Generating response...');
                }
            },
            // onToolApproval - HITL tool approval request
            (approvalData) => {
                handleToolApproval(approvalData, agent);
            },
            // onVisionStatus - vision pre-processing progress
            (visionData) => {
                if (visionData.status === 'processing') {
                    const count = visionData.count || 1;
                    updateLoadingText(`Analyzing ${count} image${count > 1 ? 's' : ''} with vision model...`);
                } else if (visionData.status === 'complete') {
                    const count = visionData.count || 1;
                    const ms = visionData.duration_ms;
                    const timeStr = ms ? ` (${(ms / 1000).toFixed(1)}s)` : '';
                    updateLoadingText(`Described ${count} image${count > 1 ? 's' : ''}${timeStr}. Generating...`);
                    toolsUsed['vision'] = (toolsUsed['vision'] || 0) + (visionData.count || 1);
                } else if (visionData.status === 'error') {
                    updateLoadingText('Vision failed. Generating...');
                }
            },
            // onDocReady - document created from response
            (docData) => {
                if (docData.filename && docData.url) {
                    toolsUsed['doc_create'] = (toolsUsed['doc_create'] || 0) + 1;
                    pendingDocLink = docData;
                }
            },
            // onDocStatus - doc create intent detection progress
            (statusData) => {
                if (statusData.status === 'detecting') {
                    updateLoadingText('Detecting document type...');
                } else if (statusData.status === 'detected') {
                    updateLoadingText(`Creating ${statusData.format?.toUpperCase() || 'document'}... Generating...`);
                } else if (statusData.status === 'error') {
                    updateLoadingText('Generating...');
                }
            },
            // onThinkingChunk - inter-step reasoning streamed during tool loops
            (chunk) => {
                if (!assistantDiv) {
                    assistantDiv = addMessage('assistant', '', { header: assistantName, extracting: true, running: true });
                }
                thinkingText += chunk;
                addThinkingToMessage(assistantDiv, thinkingText, false);
                thinkingShown = true;
            }
        );
    } catch (e) {
        flushPendingRender();
        hideLoading();
        const cooldownTriggered = recordFailure();
        if (cooldownTriggered) {
            startCooldownUI(COOLDOWN_MS);
        }
        const errorDiv = addMessage('assistant', `Error: ${e.message}`, { header: assistantName });
        errorDiv.style.color = '#f87171';
        addRetryButton(errorDiv, cooldownTriggered);
    }

    hideLoading();
    state.isStreaming = false;
    setStreamingState(false);
}
